// ============================================================
// MTM CREATOR — made-to-measure curtain builder for the product page
// ============================================================
//
// Replaces the standalone `mtm-configurator` section: each fabric is now its own
// Shopify product, and this builder is mounted on that product's page behind a
// "Create your curtains" button (see made-to-measure-designs/).
//
// PRICING / TRUST MODEL (unchanged from the cart-transform migration):
//   • Live display prices come from the Laravel middleware, which proxies illumin8
//     server-side (hides the API key, adds CORS).
//   • On add-to-bag the middleware recomputes the price and returns it HMAC-signed:
//     { sig_v, price_pence, expires_at, sig }.
//   • The product's own variant is added with that signed price + config as line-item
//     properties; a Cart Transform function verifies the signature and overrides the
//     line price, and a Cart & Checkout Validation function blocks a bad signature.
//   The client never chooses the price that is charged — it only requests a quote.
//   There is ONE price and it is signed, so the figure charged cannot be swapped after the
//   quote is issued. NO VAT is added or removed anywhere: illumin8's number is what the
//   tiles show and what the cart takes. See mtm-hmac-canonical.md §v2 → v3 for the version
//   where it wasn't, and the configurator showed £191.77 while the cart took £230.13.
//
// MAPPING THE DESIGN ONTO illumin8:
//   The design's "heading type" tile is really TWO illumin8 concepts at once — a
//   blindtypeid AND a Finish choice within it — because 200% pencil/pinch, 260%
//   triple pinch, 170% eyelet and 180% wave are separate blind types. Likewise the
//   design's Lining + Lining colour are one illumin8 choice (Lining Type), not two.
//   HEADINGS/LININGS below is that mapping, keyed on ChoiceCode rather than ChoiceID:
//   codes are the values the EDI purchase order needs, and they read as themselves
//   in a diff. The numeric ChoiceIDs the pricing API wants are resolved at runtime
//   from the live option menu, which also tells us what is actually orderable.
// ============================================================

(function () {
	'use strict';

	// The signature layout this theme writes, mirrored in `_sig_v` on the cart line. Must
	// match MtmPriceSigner::VERSION (middleware) and mtm_sig::SIG_VERSION (functions).
	var MTM_SIG_VERSION = '3';

	// illumin8 OptionGroup_IDs the UI drives explicitly.
	//
	// Curtains default every OTHER group to its cheapest choice — they return few groups
	// and all are low-stakes. Blinds do NOT: Roman returns 17 groups and Roller 18, almost
	// all £0, so "cheapest" resolves to whatever illumin8 happens to list first. Simulated
	// against the live menu that orders a Roller with a custom-colour side channel, a ROMAN
	// bottom bar, black accessories and FR chemical treatment — priced correctly, and the
	// wrong physical blind. The blind flows use BLIND_DEFAULTS below instead, and refuse to
	// quote when a group has no entry.
	var GROUP = {
		FINISH: 348, // curtain heading / roller headrail finish
		PANEL: 373,
		LINING: 347,
		ADDITIONAL_LINING: 380,
		EYELET: 374,
		TRACK: 249,
		SYSTEM: 195, // roman headrail system
		STYLE: 245, // roman fold style — illumin8 name the group "Type"
		CHAIN: 138,
		CONTROL_SIDE: 333,
		MOTOR: 287,
		MOTOR_REMOTE: 366,
		CHARGER: 341,
		HUB: 385,
	};

	// Fitting Type → illumin8 measureid. Verified against the live gateway: fabricprice
	// returns an IDENTICAL price for 69, 70 and 83, so this carries no price, only the
	// manufacturing instruction (whether illumin8 deduct for the recess or build to size).
	var MEASURE = { RECESS: 69, EXACT: 70 };

	// The two dimensions a heading can state a limit on, with the words the messages
	// about them use, in the order the size step asks for them.
	var DIMENSIONS = [
		{ field: 'width', key: 'widthMm', label: 'Width', adverb: 'wide' },
		{ field: 'drop', key: 'dropMm', label: 'Drop', adverb: 'in drop' },
	];

	// ---------- Selection data ----------

	// measureId per heading: the drop is measured from a different datum for eyelet
	// curtains (top of the pole) than for pleated ones (underside of the ring), which is
	// what the "How to measure" guide tells the shopper to do.
	//   83 = Hem to Hem, 84 = Pole to Hem.
	//
	// widthMm and dropMm are the made-up size illumin8 can build each heading to, in
	// millimetres. The shopper enters a size before choosing a heading, so these cannot
	// be enforced at the size step — they gate the heading tiles instead. A heading that
	// states neither is limited only by the product's own size range; the curtain
	// headings all share one drop, so none of them states a dropMm.
	var HEADINGS = [
		{
			key: 'pencil-3',
			label: 'Pencil Pleat 3"',
			widthMm: { min: 650, max: 5200 },
			blindtypeId: 548,
			finish: 'PENC-0200',
			measureId: 83,
		},
		{
			key: 'pencil-6',
			label: 'Pencil Pleat 6"',
			widthMm: { min: 650, max: 5200 },
			blindtypeId: 548,
			finish: 'PENC-0201',
			measureId: 83,
		},
		{
			key: 'dpinch-4',
			label: 'Double Pinch Pleat 4"',
			widthMm: { min: 650, max: 5200 },
			blindtypeId: 548,
			finish: 'PINC-0200',
			measureId: 83,
		},
		{
			key: 'dpinch-6',
			label: 'Double Pinch Pleat 6"',
			widthMm: { min: 650, max: 5200 },
			blindtypeId: 548,
			finish: 'PINC-0206',
			measureId: 83,
		},
		// Motorisation is offered only where illumin8 actually carry it. Blindtypes 548
		// (pencil pleat, double pinch) and 547 (eyelet) return NO Motor, Track, Charger or
		// Remote group at all, so those headings stay manual and show no control step.
		{
			key: 'tpinch-4',
			label: 'Triple Pinch Pleat 4"',
			widthMm: { min: 500, max: 4000 },
			blindtypeId: 549,
			finish: 'PINC-0260',
			measureId: 83,
			motorisable: true,
			manualTrack: 'CURC-0027',
			motorTrack: 'MOTC-RALWL',
			motor: 'MOTC-0145',
		},
		{
			key: 'tpinch-6',
			label: 'Triple Pinch Pleat 6"',
			widthMm: { min: 500, max: 4000 },
			blindtypeId: 549,
			finish: 'PINC-0266',
			measureId: 83,
			motorisable: true,
			manualTrack: 'CURC-0027',
			motorTrack: 'MOTC-RALWL',
			motor: 'MOTC-0145',
		},
		{
			key: 'eyelet',
			label: 'Eyelet (40mm)',
			widthMm: { min: 765, max: 6118 },
			blindtypeId: 547,
			finish: 'EYEL-0170',
			measureId: 84,
			extra: 'eyelet',
		},
		// Wave cannot hang off a pole, so a wave track is always supplied and always
		// priced in — the corded one when manual, the wireless one when motorised.
		{
			key: 'wave',
			label: 'Wave',
			widthMm: { min: 522, max: 5578 },
			blindtypeId: 583,
			finish: 'WAVE',
			measureId: 83,
			manualTrack: 'CURC-0019',
			manualTrackLabel: 'Included',
			motorisable: true,
			motorTrack: 'MOTC-RALWAVE',
			motor: 'MOTC-0145',
		},
	];

	var HEADING_ICONS = {
		'pencil-3': 'mtm-i-pencil3',
		'pencil-6': 'mtm-i-pencil6',
		'dpinch-4': 'mtm-i-dpinch4',
		'dpinch-6': 'mtm-i-dpinch6',
		'tpinch-4': 'mtm-i-tpinch4',
		'tpinch-6': 'mtm-i-tpinch6',
		eyelet: 'mtm-i-eyelet',
		wave: 'mtm-i-wave',
	};

	var PANELS = [
		{ key: 'single', label: 'Single', code: 'CURT-0006', icon: 'mtm-i-single' },
		{ key: 'pair', label: 'Pair', code: 'CURT-0007', icon: 'mtm-i-pair' },
	];

	// 40mm rings only — the design offers one eyelet size, so the 50mm codes are not exposed.
	var EYELET_COLOURS = [
		{
			key: 'antique',
			label: 'Antique',
			code: 'CURT-0003',
			icon: 'mtm-i-ring-antique',
		},
		{ key: 'gold', label: 'Gold', code: 'CURT-0002', icon: 'mtm-i-ring-gold' },
		{
			key: 'gunmetal',
			label: 'Gunmetal',
			code: 'CURT-0004',
			icon: 'mtm-i-ring-gunmetal',
		},
		{
			key: 'satin',
			label: 'Satin',
			code: 'CURT-0008',
			icon: 'mtm-i-ring-satin',
		},
		{
			key: 'silver',
			label: 'Silver',
			code: 'CURT-0001',
			icon: 'mtm-i-ring-silver',
		},
		// The workbook lists Black as CURC-0009; group 374 actually carries it as
		// CURT-0009, like its five siblings. The CURC- prefix is a typo in the sheet.
		{
			key: 'black',
			label: 'Black',
			code: 'CURT-0009',
			icon: 'mtm-i-ring-black',
		},
	];

	// One illumin8 Lining Type choice per (lining × colour) pair.
	var LININGS = [
		{
			key: 'poly-cotton',
			label: 'Poly Cotton',
			icon: 'mtm-i-lining-polycotton',
			description:
				'A lightweight lining that helps protect your curtain fabric from sunlight while softly filtering the light entering the room. Available in Ivory and White. 75% polyester, 25% cotton.',
			colours: { ivory: 'LINI-0001', white: 'LINI-0008' },
		},
		{
			key: 'satin',
			label: 'Satin',
			icon: 'mtm-i-lining-satin',
			description:
				'A medium-weight lining with a smooth satin finish, adding a more luxurious feel while gently filtering the light. Available in Ivory and White. 52% polyester, 48% cotton.',
			colours: { ivory: 'LINI-0007', white: 'LINI-0002' },
		},
		{
			key: 'blackout',
			label: 'Blackout',
			icon: 'mtm-i-lining-blackout',
			description:
				'A heavyweight lining designed to block out light and help regulate the temperature of your room, retaining warmth in winter and keeping it cooler in summer. Available in Ivory and White. 100% polyester.',
			colours: { ivory: 'LINI-0004', white: 'LINI-0003' },
		},
	];

	var LINING_COLOURS = [
		{ key: 'ivory', label: 'Ivory', swatch: '#f7f4dd' },
		{ key: 'white', label: 'White', swatch: '#ffffff' },
	];

	var INTERLINING = [
		{ key: 'yes', label: 'Yes', code: 'LINI-0006' },
		{ key: 'no', label: 'No', code: 'LINI-00060' },
	];

	// ---------- shared across ranges ----------

	// Fitting Type — blinds only. Curtains hang off a pole or track and have no recess.
	var FITTINGS = [
		{
			key: 'recess',
			label: 'Recess',
			icon: 'mtm-i-fit-recess',
			measureId: MEASURE.RECESS,
		},
		{
			key: 'exact',
			label: 'Exact',
			icon: 'mtm-i-fit-exact',
			measureId: MEASURE.EXACT,
		},
	];

	// Control Side carries different ChoiceCodes on different blind types — LHC/RHC on the
	// blinds and on curtain 548, SPE001/SPE002 on Wave. Resolved against the live menu.
	var CONTROL_SIDES = [
		{ key: 'left', label: 'Left', codes: ['LHC', 'SPE001'] },
		{ key: 'right', label: 'Right', codes: ['RHC', 'SPE002'] },
	];

	var CHAINS = [
		{
			key: 'white',
			label: 'White Plastic',
			code: 'ROLC-0020WH',
			swatch: '#ffffff',
		},
		{
			key: 'black',
			label: 'Black Plastic',
			code: 'ROLC-0020BK',
			swatch: '#1c1c1c',
		},
		{
			key: 'nickel',
			label: 'Nickel Metal',
			code: 'ROLC-0031',
			swatch: '#b9bec2',
		},
		{
			key: 'brass',
			label: 'Brass Metal',
			code: 'ROMC-0014',
			swatch: '#b08d46',
		},
	];

	var REMOTES = [
		{ key: 'none', label: 'No Remote', code: 'MOTO-0004NR' },
		{ key: 'ch1', label: '1 Channel', code: 'MOTC-0102' },
		{ key: 'ch5', label: '5 Channel', code: 'MOTC-0103' },
		{ key: 'ch15', label: '15 Channel', code: 'MOTC-0104' },
	];

	// The workbook's charger/hub "Yes" codes — MOTC-0107 and MOTC-0106 — do not exist in
	// the pricing API on ANY of the eight blind types, at any width, against any fabric.
	// These are the real items from groups 341 and 385, filtered to the ones compatible
	// with the Motion motors the workbook specifies: the same groups also list Somfy and
	// Tahoma hardware, which would not work with a Motion motor. See
	// docs/illumin8-field-queries-round-2.md.
	var CHARGERS = [
		{ key: 'none', label: 'No charger', code: 'MOTO-0002NR' },
		{ key: 'usbc', label: 'USB-Type C cable (3m)', code: 'MOTC-0132' },
	];

	var HUBS = [
		{ key: 'none', label: 'No hub', code: 'HUB00' },
		{ key: 'bridge', label: 'Motion Matter Bridge', code: 'MOTC-0200' },
	];

	var CONTROL_TYPES = [
		{ key: 'manual', label: 'Manual' },
		{ key: 'motorised', label: 'Motorised' },
	];

	// ---------- Roman ----------

	// As with curtains, one design tile is TWO illumin8 concepts: a blindtypeid plus a
	// System choice (group 195). Manual and motorised are separate blind types.
	//
	// The three systems that carry a size limit carry the same one on both blind ranges:
	// the motorised headrails are held to the widths their motors drive, and no drill is
	// held to what a bracketless fitting can hold up. Classic and Cassette are made to
	// whatever the product itself allows, so they state no limit at all.
	var ROMAN_HEADINGS = [
		{
			key: 'classic',
			label: 'Classic',
			blindtypeId: 581,
			finish: 'DLXSYS',
			control: 'manual',
			icon: 'mtm-i-rom-classic',
			rail: 'classic',
		},
		{
			key: 'cassette',
			label: 'Cassette',
			blindtypeId: 581,
			finish: 'EVOSYS',
			control: 'manual',
			icon: 'mtm-i-rom-cassette',
			rail: 'cassette',
		},
		{
			key: 'nodrill',
			label: 'Classic No Drill',
			widthMm: { min: 300, max: 2000 },
			dropMm: { min: 300, max: 2000 },
			blindtypeId: 581,
			finish: 'SIMTWIST',
			control: 'manual',
			icon: 'mtm-i-rom-nodrill',
			rail: 'nodrill',
			// "No Drill option should be unselectable if Exact fitting type is selected."
			requiresFitting: 'recess',
		},
		{
			key: 'classic-mot',
			label: 'Classic Motorised',
			widthMm: { min: 680, max: 2400 },
			dropMm: { min: 300, max: 2400 },
			blindtypeId: 582,
			finish: 'DLXSYS', // illumin8, 2026-10-01: Classic is Classic Open, motorised or not
			control: 'motorised',
			icon: 'mtm-i-rom-classic-mot',
			rail: 'classic',
			motor: 'MOTC-0126',
		},
		{
			key: 'cassette-mot',
			label: 'Cassette Motorised',
			widthMm: { min: 560, max: 2600 },
			dropMm: { min: 300, max: 3000 },
			blindtypeId: 582,
			finish: 'EVOSYS',
			control: 'motorised',
			icon: 'mtm-i-rom-cassette-mot',
			rail: 'cassette',
			motor: 'MOTC-0101BT',
		},
	];

	// Fold style — group 245, which illumin8 call "Type". Both choices are £0.00 on 581 and
	// 582 (checked against the live menu), so this is make-up rather than an upgrade.
	//
	// Until this step existed every Roman blind was sent CASCADE from BLIND_DEFAULTS, so an
	// order placed today is unchanged if the shopper picks Cascade. The group also carries
	// USA-Stacked, USA-Cascade, USA-Hobbled, USA-Elite Cascade and Cascade Luxe; only the two
	// the client sells are offered.
	//
	// The design calls this step "Style"; illumin8 use that word for group 239, a different
	// thing we send STY048 (Standard) for. 245 is the one the shopper is choosing.
	var ROMAN_STYLES = [
		{
			key: 'stacked',
			label: 'Stacked',
			code: 'STACKED',
			// Keyed by the chosen heading's `rail`, so the tile shows that headrail.
			icons: {
				classic: 'mtm-i-rom-stacked-classic',
				cassette: 'mtm-i-rom-stacked-cassette',
				nodrill: 'mtm-i-rom-stacked-nodrill',
			},
			description:
				'Stacked creates a clean uniform look with all folds sitting neatly on the same level when open.',
		},
		{
			key: 'cascade',
			label: 'Cascade',
			code: 'CASCADE',
			icons: {
				classic: 'mtm-i-rom-cascade-classic',
				cassette: 'mtm-i-rom-cascade-cassette',
				nodrill: 'mtm-i-rom-cascade-nodrill',
			},
			description:
				'Cascade offers a stepped finish, with folds 10–25mm apart (depending on the blind’s drop), adding a decorative touch but taking up slightly more window space.',
		},
	];

	// One illumin8 Lining Type choice per (lining × colour). Bonded linings are Ivory only,
	// which is what the workbook shows and what group 347 actually carries.
	var ROMAN_LININGS = [
		{
			key: 'satin',
			label: 'Satin',
			icon: 'mtm-i-lining-satin',
			description:
				'A medium-weight lining with a smooth satin finish, gently filtering the light. Available in Ivory and White.',
			colours: { ivory: 'LINI-0007', white: 'LINI-0002' },
		},
		{
			key: 'blackout',
			label: 'Blackout',
			icon: 'mtm-i-lining-blackout',
			description:
				'A heavyweight lining designed to block out light and help regulate room temperature. Available in Ivory and White.',
			colours: { ivory: 'LINI-0004', white: 'LINI-0003' },
		},
		{
			key: 'bonded-satin',
			label: 'Bonded Satin',
			icon: 'mtm-i-lining-bonded-satin',
			description:
				'Satin lining bonded to the face fabric for a flatter, crisper fold. Available in Ivory.',
			colours: { ivory: 'LINI-0009' },
		},
		{
			key: 'bonded-blackout',
			label: 'Bonded Blackout',
			icon: 'mtm-i-lining-bonded-blackout',
			description:
				'Blackout lining bonded to the face fabric, combining full light block with a flatter fold. Available in Ivory.',
			colours: { ivory: 'LINI-0010' },
		},
	];

	// ---------- Roller ----------

	var ROLLER_HEADINGS = [
		{
			key: 'classic',
			label: 'Classic',
			blindtypeId: 566,
			finish: 'CLASSIC',
			control: 'manual',
			icon: 'mtm-i-rol-classic',
		},
		{
			key: 'cassette',
			label: 'Cassette',
			blindtypeId: 566,
			finish: 'EVO',
			control: 'manual',
			icon: 'mtm-i-rol-cassette',
		},
		{
			key: 'nodrill',
			label: 'Classic No Drill',
			widthMm: { min: 300, max: 2000 },
			dropMm: { min: 300, max: 2000 },
			blindtypeId: 566,
			finish: 'SMTWRL',
			control: 'manual',
			icon: 'mtm-i-rol-nodrill',
			requiresFitting: 'recess',
		},
		{
			key: 'classic-mot',
			label: 'Classic Motorised',
			widthMm: { min: 560, max: 2400 },
			dropMm: { min: 300, max: 2400 },
			blindtypeId: 567,
			finish: 'CLASSIC',
			control: 'motorised',
			icon: 'mtm-i-rol-classic-mot',
			motor: 'MOTC-0101BT',
		},
		{
			key: 'cassette-mot',
			label: 'Cassette Motorised',
			widthMm: { min: 560, max: 2600 },
			dropMm: { min: 300, max: 3000 },
			blindtypeId: 567,
			finish: 'EVO',
			control: 'motorised',
			icon: 'mtm-i-rol-cassette-mot',
			motor: 'MOTC-0101BT',
		},
	];

	// Base cloth is NOT an option group — illumin8 hold it as fabric identity. The workbook
	// says to change the suffix on the fabric code (ORLA-0001TTR/TBO); the pricing API
	// takes a numeric fabricid and offers no code lookup, so each cloth carries its own
	// metafield. A product that omits one simply does not offer that cloth.
	//
	// Two cloths only: Light Filtering is the default (listed first, so it is what the
	// confirm-size step quotes against before a cloth is picked) and Blackout is the
	// upgrade. illumin8 named a third, Waterproof (CBO), in the workbook but never
	// supplied fabric ids for it, so it is not sold.
	var BASE_CLOTHS = [
		{
			key: 'light_filtering',
			label: 'Light Filtering',
			suffix: 'TTR',
			icon: 'mtm-i-cloth-light',
			description:
				'Softly diffuses daylight while keeping the room bright and private.',
		},
		{
			key: 'blackout',
			label: 'Blackout',
			suffix: 'TBO',
			icon: 'mtm-i-cloth-blackout',
			description:
				'Blocks out light almost entirely, for bedrooms and media rooms.',
		},
	];

	// Motor override: "To be used for blinds from 560mm to 650mm wide" (workbook, row 15).
	var NARROW_MOTOR = { code: 'MOTC-0161', minMm: 560, maxMm: 650 };

	// ---------- explicit defaults for the blind ranges ----------
	//
	// blindtypeId → OptionGroup_ID → ChoiceCode, or { <headingKey>: code, _: code }.
	// Every group the UI does not ask about MUST appear here; a group with no entry stops
	// the quote rather than being guessed at, so a new group from illumin8 surfaces as an
	// error instead of a silently wrong blind.
	//
	// Values marked ⚠ are a judgement call rather than something the workbook states, and
	// are listed for sign-off in docs/illumin8-field-queries-round-2.md.
	var BLIND_DEFAULTS = {
		// Roman Manual
		581: {
			152: 'PIL077', // Pull — none; a chain-operated roman has no pull
			185: { nodrill: 'ROMC-0037', _: 'ROMC-0025' }, // ⚠ Control Kit: child-safe unit; Simply Twist control for no-drill
			204: 'BRASTDS', // ⚠ Brackets — standard
			239: 'STY048', // Style — Standard
			280: 'SPE134', // Same Room — No; each line is ordered independently
			288: 'VALNO', // Valance Type — none
			290: 'PM05', // Align and Match — none
			311: { cassette: 'EVOMF', _: 'EVOFINNR' }, // ⚠ Evo Finish — metal, only when a cassette is chosen
			350: { cassette: 'ROLC-0006WH', _: 'EVO0-0000' }, // ⚠ Evo cassette — M, White
			377: 'SPE167', // Stitch Style — Standard
			401: 'FLOC-0001', // Fabric Location — Front
			435: 'SERV000', // Service — none
		},
		// Roman Motor
		582: {
			204: 'BRASTDS', // ⚠
			290: 'PM05',
			311: { 'cassette-mot': 'EVOMF', _: 'EVOFINNR' }, // ⚠
			350: { 'cassette-mot': 'ROLC-0006WH', _: 'EVO0-0000' }, // ⚠
			377: 'SPE167',
			393: 'INTNO', // Intermediate Bracket — Not Required
			401: 'FLOC-0001',
			435: 'SERV000',
		},
		// Roller Manual
		566: {
			134: 'ROLC-0008PLA', // ⚠ Bottom Bar — plastic white (ROMC-0006, which "cheapest" picks, is a ROMAN bar)
			152: 'PIL077',
			183: 'ROLC-0021NR', // Bracket Cover — none
			185: { nodrill: 'RC762', _: 'ROLC-0010WH' }, // ⚠ Control Kit: 38mm white; 247 no-drill clutch for no-drill
			204: 'BRASTDS', // ⚠
			205: 'ROLB-0000', // Braid — none (its only choice)
			305: 'ACCWHI', // ⚠ Accessories — White
			311: { cassette: 'EVOMF', _: 'EVOFINNR' }, // ⚠
			312: 'BBARMCF', // ⚠ Bottom Bar Finish — wrapped in the matching design fabric
			350: { cassette: 'ROLC-0006WH', _: 'EVO0-0000' }, // ⚠
			351: 'ROLL-0001', // Roll Type — Standard (back roll)
			378: 'ORLBBRNO', // B-Bar Fabric colour — none (the bar matches the blind)
			389: 'CUNO', // Customer Design — No
			391: 'SCHNLNR', // Side Channel — none
			398: 'FABTYPE', // Fabric Type — none; NOT FRTREAT, which "cheapest" would pick
		},
		// Roller Motor
		567: {
			134: 'ROLC-0008PLA', // ⚠
			152: 'PIL077',
			205: 'ROLB-0000',
			305: 'ACCWHI', // ⚠
			311: { 'cassette-mot': 'EVOMF', _: 'EVOFINNR' }, // ⚠
			312: 'BBARMCF', // ⚠
			350: { 'cassette-mot': 'ROLC-0006WH', _: 'EVO0-0000' }, // ⚠
			351: 'ROLL-0001',
			389: 'CUNO',
			391: 'SCHNLNR',
			393: 'INTNO',
			398: 'FABTYPE',
		},
	};

	// ---------- flows ----------

	var FLOWS = {
		curtain: {
			key: 'curtain',
			noun: 'curtains',
			headings: HEADINGS,
			headingGroup: GROUP.FINISH,
			linings: LININGS,
			steps: ['size', 'confirm', 'heading', 'control', 'lining', 'summary'],
			stepLabels: {
				size: 'Select size',
				confirm: 'Confirm size',
				heading: 'Heading',
				control: 'Control type',
				lining: 'Lining',
				summary: 'Summary',
			},
			// Shown as "Base Price" at the confirm-size step, before a heading is chosen.
			defaultBlindtypeId: 548,
			defaultMeasureId: 83,
		},
		roman: {
			key: 'roman',
			noun: 'blind',
			headings: ROMAN_HEADINGS,
			headingGroup: GROUP.SYSTEM,
			linings: ROMAN_LININGS,
			steps: [
				'size',
				'confirm',
				'heading',
				'control',
				'style',
				'lining',
				'summary',
			],
			stepLabels: {
				size: 'Select size',
				confirm: 'Confirm size',
				heading: 'Heading',
				control: 'Control type',
				style: 'Style',
				lining: 'Lining',
				summary: 'Summary',
			},
			defaultBlindtypeId: 581,
			defaultMeasureId: MEASURE.RECESS,
			defaults: BLIND_DEFAULTS,
		},
		roller: {
			key: 'roller',
			noun: 'blind',
			headings: ROLLER_HEADINGS,
			headingGroup: GROUP.FINISH,
			steps: ['size', 'confirm', 'heading', 'control', 'cloth', 'summary'],
			stepLabels: {
				size: 'Select size',
				confirm: 'Confirm size',
				heading: 'Heading',
				control: 'Control type',
				cloth: 'Base cloth',
				summary: 'Summary',
			},
			defaultBlindtypeId: 566,
			defaultMeasureId: MEASURE.RECESS,
			defaults: BLIND_DEFAULTS,
		},
	};

	// ============================================================
	// Units
	// ============================================================
	//
	// Everything downstream of this file — the price request, the HMAC the middleware
	// signs, the illumin8 purchase order — is millimetres, and the shopper's choice of
	// unit does not change that. state.widthMm / state.dropMm are the only measurements
	// the creator holds; mm, cm and inches are three ways of writing them down.

	var MM_PER_INCH = 25.4;

	var UNITS = [
		{ key: 'mm', label: 'mm' },
		{ key: 'cm', label: 'cm' },
		{ key: 'in', label: 'inches' },
	];

	// The unit is a shopper preference rather than part of the configuration, so it
	// outlives the page: someone who works in inches should not reselect it on every
	// fabric they look at.
	var UNIT_STORAGE_KEY = 'mtm-unit';

	// Inches are entered as whole inches plus an eighth — the granularity the trade
	// quotes in, and the one the reference site offers. An eighth is 3.175mm, coarser
	// than anything else in the flow, so snapMmToEighths owns all of that rounding.
	var EIGHTHS = [
		{ value: 0, label: '0' },
		{ value: 1, label: '\u215B' },
		{ value: 2, label: '\u00BC' },
		{ value: 3, label: '\u215C' },
		{ value: 4, label: '\u00BD' },
		{ value: 5, label: '\u215D' },
		{ value: 6, label: '\u00BE' },
		{ value: 7, label: '\u215E' },
	];

	function mmFromCm(cm) {
		return Math.round(cm * 10);
	}

	// Inches round DOWN to the millimetre. The loss is under 1mm, and on an exact fit
	// an undersize is harmless where an oversize does not hang.
	function mmFromInches(whole, eighths) {
		return Math.floor((whole + eighths / 8) * MM_PER_INCH);
	}

	// The nearest eighth to a millimetre value, stepped back inside [minMm, maxMm] when
	// that nearest eighth converts to a length outside them. Without the guard the
	// minimum shown in the placeholder can be a value the validator then rejects, which
	// is the classic way to lose an order. Pass null bounds to snap without guarding.
	function snapMmToEighths(mm, minMm, maxMm) {
		var eighths = Math.round((mm / MM_PER_INCH) * 8);
		// Two passes is enough: an eighth is 3.175mm, and the guard only ever has to
		// undo the half-eighth the rounding itself introduced.
		for (var i = 0; i < 2; i++) {
			var back = mmFromInches(0, eighths);
			if (maxMm != null && back > maxMm) eighths -= 1;
			else if (minMm != null && back < minMm) eighths += 1;
			else break;
		}
		if (eighths < 0) eighths = 0;
		return { whole: Math.floor(eighths / 8), eighths: eighths % 8 };
	}

	// "23", "23 5/8" — as whole inches and a vulgar fraction.
	function inchesLabel(parts) {
		var fraction = findBy(EIGHTHS, 'value', parts.eighths);
		return parts.whole + (parts.eighths ? ' ' + fraction.label : '');
	}

	function readStoredUnit() {
		try {
			var stored = window.localStorage.getItem(UNIT_STORAGE_KEY);
			if (findBy(UNITS, 'key', stored)) return stored;
		} catch (error) {
			// Private-mode Safari throws on localStorage. Centimetres is the default.
		}
		return 'cm';
	}

	function storeUnit(unit) {
		try {
			window.localStorage.setItem(UNIT_STORAGE_KEY, unit);
		} catch (error) {
			// Not being able to remember the choice is not worth failing the step over.
		}
	}

	// ============================================================
	// Helpers
	// ============================================================

	function formatMoney(amount) {
		return new Intl.NumberFormat('en-GB', {
			style: 'currency',
			currency: 'GBP',
		}).format(amount);
	}

	// The price after the made-to-measure discount (config.discountPercent). The discount
	// itself is a Shopify automatic discount taken in the cart; this only shows it early.
	// The saving is rounded to the penny before it comes off, as Shopify does, so "Now"
	// matches the cart.
	function discountedPrice(amount, percent) {
		var pence = Math.round(amount * 100);
		var saving = Math.round((pence * percent) / 100);
		return (pence - saving) / 100;
	}

	// A millimetre value written in the shopper's unit. Limits are held in millimetres
	// throughout — 6118mm reads back as "611.8cm" or "240 7/8 in" depending on who is
	// looking at it.
	function formatLength(mm, unit) {
		if (mm == null || isNaN(mm)) return '\u2014';
		if (unit === 'in') return inchesLabel(snapMmToEighths(mm)) + ' in';
		if (unit === 'cm') return String(Math.round(mm) / 10) + 'cm';
		return Math.round(mm) + 'mm';
	}

	// The same length with the millimetres spelled out after it. This is what the cart
	// line, the confirmation email and the packing slip carry, so a shopper who ordered
	// in inches can check the size against the figure the workroom builds to.
	function formatLengthFull(mm, unit) {
		if (mm == null || isNaN(mm)) return '\u2014';
		if (unit === 'mm') return formatLength(mm, unit);
		return formatLength(mm, unit) + ' (' + Math.round(mm) + 'mm)';
	}

	// "2026-09-19" → "19 September", or "19 September 2027" when it is not this year.
	// The date illumin8 expect a deactivated fabric back; built from the parts rather
	// than parsed, because `new Date('2026-09-19')` is UTC midnight and reads as the
	// 18th anywhere west of Greenwich.
	function formatEta(iso) {
		var parts = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(iso || ''));
		if (!parts) return null;

		var date = new Date(
			Number(parts[1]),
			Number(parts[2]) - 1,
			Number(parts[3]),
		);
		if (isNaN(date.getTime())) return null;

		return date.toLocaleDateString('en-GB', {
			day: 'numeric',
			month: 'long',
			year:
				date.getFullYear() === new Date().getFullYear() ? undefined : 'numeric',
		});
	}

	// What to say about a /mtm/price refusal. The middleware answers 422 both for a
	// configuration it cannot price and for a fabric it will not price, and the two need
	// different words: the first is a dead end, the second is a fabric that may be back.
	//
	// This is the backstop for everything the browser cannot see — the tab left open
	// overnight, the cached page, the fabric deactivated since the availability call.
	// Returns null for anything unrecognised, so an unknown refusal keeps the generic
	// sentence rather than inventing a reason for it.
	function quoteRefusalMessage(payload) {
		if (!payload || typeof payload !== 'object') return null;

		if (payload.reason === 'fabric_unavailable') {
			var eta = formatEta(payload.eta);
			return (
				'This fabric is currently unavailable, so we cannot make it to measure ' +
				'at the moment. ' +
				(eta ? 'We expect it back on ' + eta + '. ' : '') +
				'You can still order a sample.'
			);
		}

		// Recoverable, and says so: the middleware could not read its own availability
		// data and refuses to sign rather than guess. Its own sentence already asks for a
		// retry, so it is passed through as written.
		if (payload.reason === 'availability_check_failed') {
			return (
				payload.error ||
				'We cannot confirm fabric availability right now. Please try again.'
			);
		}

		return null;
	}

	function escapeHtml(value) {
		return String(value == null ? '' : value).replace(
			/[&<>"']/g,
			function (char) {
				return {
					'&': '&amp;',
					'<': '&lt;',
					'>': '&gt;',
					'"': '&quot;',
					"'": '&#39;',
				}[char];
			},
		);
	}

	function icon(id, className) {
		return (
			'<svg class="' +
			className +
			'" aria-hidden="true" focusable="false"><use href="#' +
			id +
			'"></use></svg>'
		);
	}

	function findBy(list, key, value) {
		for (var i = 0; i < list.length; i++) {
			if (list[i][key] === value) return list[i];
		}
		return null;
	}

	// Markets and language subfolders live in the root prefix, so every cart URL has to
	// be built from it — '/cart/add.js' would 404 on /en-eu.
	function cartRoot() {
		return (
			(window.Shopify && window.Shopify.routes && window.Shopify.routes.root) ||
			'/'
		);
	}

	// Prestige 10.6 re-renders the cart drawer from section HTML that comes back with
	// the add, and its `cart:change` handler reads `cart.sections[id]` *before* it opens
	// the drawer — so a cart without them throws and the drawer stays shut. The section
	// ids are collected exactly the way the theme's own product form collects them.
	// Prestige 5.7 (the develop theme) has no listener for this event, so the array comes
	// back empty there and every sections-aware branch below is skipped.
	function bundledSections() {
		var sections = [];
		document.documentElement.dispatchEvent(
			new CustomEvent('cart:prepare-bundled-sections', {
				bubbles: true,
				detail: { sections: sections },
			}),
		);
		return sections;
	}

	// The cart line for a variant, if the shopper already has one.
	function findCartLine(cart, variantId) {
		var items = (cart && cart.items) || [];
		for (var i = 0; i < items.length; i++) {
			if (Number(items[i].variant_id) === Number(variantId)) return items[i];
		}
		return null;
	}

	// Samples are posted out free, so the bag holds at most this many at once.
	var SAMPLE_LIMIT = 10;

	// Every sample product shares one product type (config.sampleProductType, read off
	// the sample product in Liquid) because the type is the only sample-identifying
	// field /cart.js returns — line items carry no tags. Quantities rather than lines:
	// the cart drawer lets a shopper raise the quantity of a sample already in the bag.
	function countCartSamples(cart, productType) {
		var type = String(productType || '')
			.trim()
			.toLowerCase();
		// No shared type means no way to tell a sample from anything else in the bag.
		// Counting nothing keeps the button working rather than capping every product.
		if (!type) return 0;

		var items = (cart && cart.items) || [];
		var total = 0;
		for (var i = 0; i < items.length; i++) {
			var itemType = String(items[i].product_type || '')
				.trim()
				.toLowerCase();
			if (itemType === type) total += Number(items[i].quantity) || 0;
		}
		return total;
	}

	// ============================================================
	// Debug logging
	// ============================================================
	//
	// Off by default. Turn it on for a session by adding ?mtm_debug=1 to the product
	// URL, or from the console with mtmDebug(true) — the choice is remembered in
	// localStorage, so it survives the reloads a full add-to-bag run needs. mtmDebug()
	// with no argument reports the current state, mtmDebug(false) turns it back off.
	//
	// Every middleware and cart request logs its method, full URL and (for POSTs) its
	// body; every response logs its status, how long it took and a readable summary —
	// option menus as a table of choices, prices as numbers, the signed quote with its
	// signature shortened. Cache hits are logged too, so a step that shows a price
	// without making a request does not look like a missing call.

	var DEBUG_KEY = 'mtm:debug';
	var DEBUG_STYLE = 'color:#7c3aed;font-weight:bold';
	var DEBUG_MAX_ROWS = 200;
	var debugOn = readDebugFlag();
	var debugSeq = 0;

	function readDebugFlag() {
		try {
			if (window.MTM_DEBUG) return true;
			var param = new URLSearchParams(window.location.search).get('mtm_debug');
			if (param !== null) {
				var on = param !== '0' && param !== 'false';
				window.localStorage.setItem(DEBUG_KEY, on ? '1' : '0');
				return on;
			}
			return window.localStorage.getItem(DEBUG_KEY) === '1';
		} catch (error) {
			// Blocked storage (private browsing, cookie-less embeds) throws on read.
			// Debug logging is never worth breaking the builder over.
			return false;
		}
	}

	function setDebug(on) {
		if (on === undefined) return debugOn;
		debugOn = !!on;
		try {
			window.localStorage.setItem(DEBUG_KEY, debugOn ? '1' : '0');
		} catch (error) {
			/* not persisted, still on for this page */
		}
		console.log(
			'%c[mtm] debug logging ' + (debugOn ? 'ON' : 'OFF'),
			DEBUG_STYLE,
		);
		return debugOn;
	}

	window.mtmDebug = setDebug;

	function debugNow() {
		return window.performance && window.performance.now
			? window.performance.now()
			: Date.now();
	}

	function debugSince(startedAt) {
		return Math.round(debugNow() - startedAt) + 'ms';
	}

	// console.group so a busy step (four menus at once) stays readable, collapsed so
	// the log reads as a list of calls until you open one.
	function debugGroup(title, body) {
		if (!debugOn) return;
		var open = console.groupCollapsed ? 'groupCollapsed' : 'group';
		console[open]('%c[mtm]%c ' + title, DEBUG_STYLE, '');
		try {
			body();
		} catch (error) {
			console.log('(could not render debug output)', error);
		}
		console.groupEnd();
	}

	function debugLine(title) {
		if (!debugOn) return;
		console.log('%c[mtm]%c ' + title, DEBUG_STYLE, '');
	}

	// Returns the id used to tie the response line back to its request.
	function debugRequest(method, url, body) {
		if (!debugOn) return 0;
		var id = ++debugSeq;
		if (body === undefined) {
			debugLine('#' + id + ' → ' + method + ' ' + url);
		} else {
			debugGroup('#' + id + ' → ' + method + ' ' + url, function () {
				console.log('body', body);
			});
		}
		return id;
	}

	function debugResponse(id, status, startedAt, payload) {
		debugGroup(
			'#' + id + ' ← ' + status + ' (' + debugSince(startedAt) + ')',
			function () {
				debugPayload(payload);
			},
		);
	}

	function debugFailure(id, startedAt, error) {
		if (error && error.mtmLogged) return;
		debugGroup(
			'#' + id + ' ✕ failed (' + debugSince(startedAt) + ')',
			function () {
				console.log(error && error.name === 'AbortError' ? 'timed out' : error);
			},
		);
	}

	function debugTable(rows) {
		if (console.table) console.table(rows);
		else console.log(rows);
	}

	// illumin8 answers everything with flat rows, and a raw dump of a 341-row option
	// menu is unreadable. Known row shapes are reduced to the fields that matter.
	function debugPayload(payload) {
		if (!Array.isArray(payload)) {
			console.log(payload);
			return;
		}
		console.log(payload.length + (payload.length === 1 ? ' row' : ' rows'));
		if (!payload.length) return;

		var rows = payload;
		if (rows.length > DEBUG_MAX_ROWS) {
			rows = rows.slice(0, DEBUG_MAX_ROWS);
			console.log(
				'(showing the first ' + DEBUG_MAX_ROWS + '; the full payload is below)',
			);
		}

		if (payload[0] && payload[0].OptionGroup_ID !== undefined) {
			debugTable(
				rows.map(function (row) {
					return {
						blindtype: row.Blindtypeid,
						groupId: row.OptionGroup_ID,
						group: (row.OptionGroup || '').trim(),
						choiceId: row.ChoiceID,
						code: (row.ChoiceCode || '').trim(),
						description: (row.ChoiceDescription || '').trim(),
						price: Number(row.SalesPrice) || 0,
					};
				}),
			);
		} else {
			debugTable(rows);
		}
		console.log('raw', payload);
	}

	// The signed quote, with the HMAC shortened: it is long, it is the least
	// interesting field on screen, and a full signature invites someone to paste one
	// into a ticket. The prices and echoed inputs are what a bad line item is read
	// against, so those are shown as they came back.
	function debugQuote(quote) {
		if (!debugOn || !quote || typeof quote !== 'object') return quote;
		var shown = {};
		Object.keys(quote).forEach(function (field) {
			shown[field] =
				field === 'sig' && typeof quote[field] === 'string'
					? quote[field].slice(0, 8) + '… (' + quote[field].length + ' chars)'
					: quote[field];
		});
		return shown;
	}

	// The grouped menu the UI actually reads, after rows for other blind types and
	// duplicate choices have been dropped — the count gap against the raw payload is
	// the fastest way to spot a collided menu.
	function debugGroups(key, groups) {
		debugGroup('menu ready ' + key, function () {
			debugTable(
				Object.keys(groups).map(function (groupId) {
					return {
						groupId: Number(groupId),
						group: groups[groupId].groupName,
						choices: groups[groupId].choices.length,
					};
				}),
			);
			console.log('groups', groups);
		});
	}

	// ============================================================
	// Middleware transport
	// ============================================================

	function getJSON(url, timeoutMs) {
		var controller = new AbortController();
		var timeoutId = setTimeout(function () {
			controller.abort();
		}, timeoutMs || 12000);

		var requestId = debugRequest('GET', url);
		var startedAt = debugNow();

		return fetch(url, {
			signal: controller.signal,
			headers: { Accept: 'application/json' },
		})
			.then(function (response) {
				if (!response.ok) {
					var httpError =
						response.status >= 500
							? new Error(
									'The pricing service is temporarily unavailable. Please try again shortly.',
								)
							: new Error(
									'That combination is not available. Please adjust your selection.',
								);
					// The gateway explains itself in the body; log that rather than the
					// shopper-facing sentence, which says nothing a developer can use.
					if (!debugOn) throw httpError;
					return response.text().then(function (body) {
						debugResponse(requestId, response.status, startedAt, body);
						httpError.mtmLogged = true;
						throw httpError;
					});
				}
				return response.json().then(function (payload) {
					debugResponse(requestId, response.status, startedAt, payload);
					return payload;
				});
			})
			.catch(function (error) {
				debugFailure(requestId, startedAt, error);
				if (error.name === 'AbortError') {
					throw new Error('Unable to calculate a price. Please try again.');
				}
				if (error instanceof TypeError) {
					throw new Error(
						'Could not reach the pricing service. Please try again shortly.',
					);
				}
				throw error;
			})
			.then(
				function (value) {
					clearTimeout(timeoutId);
					return value;
				},
				function (error) {
					clearTimeout(timeoutId);
					throw error;
				},
			);
	}

	// Flat optionprice rows → { groupId: { groupId, groupName, choices[] } }.
	//
	// Rows without an OptionGroup_ID are skipped rather than bucketed under `undefined`: the
	// gateway reports a failure as a single `{ ID: "Error - ..." }` row with HTTP 200, and
	// grouping that produces a perfectly truthy menu containing nothing the UI can find. Every
	// tile then greys out with no error shown. An unusable payload must come back empty so
	// loadOptions can reject it.
	// Rows belonging to another blind type are dropped, and duplicate choices within a group are
	// collapsed. A collided menu comes back as a merged superset of several blind types' menus —
	// one live capture asked for 548 and got 341 rows, 240 of them from 547, 549 and 583, each
	// row stating its true Blindtypeid. The middleware now refuses such a menu outright; this is
	// the second line, so that a stale cached response or an older middleware cannot put another
	// range's options in front of a shopper.
	function groupOptions(rows, blindtypeId) {
		var groups = {};
		var seen = {};
		(rows || []).forEach(function (row) {
			var groupId = row.OptionGroup_ID;
			if (groupId == null) return;
			if (
				blindtypeId != null &&
				row.Blindtypeid != null &&
				Number(row.Blindtypeid) !== Number(blindtypeId)
			) {
				return;
			}
			var key = groupId + ':' + row.ChoiceID;
			if (seen[key]) return;
			seen[key] = true;
			if (!groups[groupId]) {
				groups[groupId] = {
					groupId: groupId,
					groupName: (row.OptionGroup || '').trim(),
					choices: [],
				};
			}
			groups[groupId].choices.push({
				choiceId: row.ChoiceID,
				code: (row.ChoiceCode || '').trim(),
				description: (row.ChoiceDescription || '').trim(),
				price: Number(row.SalesPrice) || 0,
			});
		});
		return groups;
	}

	// ============================================================
	// <mtm-creator>
	// ============================================================

	function defineCreator() {
		var Creator = class extends HTMLElement {
			connectedCallback() {
				if (this._mounted) return;
				this._mounted = true;

				var configEl = this.querySelector('[data-mtm-config]');
				if (!configEl) return;

				try {
					this.config = JSON.parse(configEl.textContent);
				} catch (error) {
					return;
				}

				this.base = String(this.config.middlewareBase || '').replace(
					/\/+$/,
					'',
				);

				// An unknown or missing flow falls back to curtains rather than throwing —
				// but the Liquid will not render the creator at all without a valid
				// custom.mtm_type, so this is a belt-and-braces default.
				this.flow = FLOWS[this.config.flow] || FLOWS.curtain;

				// The theme states its size limits in centimetres; everything that measures
				// against them — validation, the placeholders, the heading gates — works in
				// millimetres, so they are converted once here.
				this.limits = {
					widthMin: mmFromCm(this.config.widthMin),
					widthMax: mmFromCm(this.config.widthMax),
					dropMin: mmFromCm(this.config.dropMin),
					dropMax: mmFromCm(this.config.dropMax),
				};

				this.stepsEl = this.querySelector('[data-mtm-steps]');
				this.startEl = this.querySelector('[data-mtm-start]');
				this.unavailableEl = this.querySelector('[data-mtm-unavailable]');
				this.drawerEl = this.querySelector('[data-mtm-drawer]');
				this.sampleErrorEl = this.querySelector('[data-mtm-sample-error]');

				// Cached middleware responses, keyed by the inputs that change the answer.
				this.optionCache = {};
				this.fabricCache = {};
				// The speculative fetches in flight, keyed by what they will cache, so a
				// re-render cannot start a second one for the same configuration.
				this.pending = {};

				// What illumin8 can currently make, once it arrives. `null` is "we have not
				// been told", which every lookup reads as available — see loadAvailability.
				this.availability = null;
				this.availabilityGeneratedAt = null;

				this.state = {
					started: false,
					step: 'size',
					furthest: 0,
					panel: 'single',
					unit: readStoredUnit(),
					widthMm: null,
					dropMm: null,
					heading: null,
					eyeletColour: null,
					lining: null,
					liningColour: null,
					interlining: null,
					// Blinds (and motorised curtains)
					fitting: null,
					controlType: null,
					chain: null,
					controlSide: null,
					style: null, // roman fold style
					remote: null,
					charger: null,
					hub: null,
					cloth: null,
					basePrice: null, // fabricprice for the confirm-size step
					total: null, // full configured price, exactly as illumin8 returned it
					loading: false,
					error: '',
					fieldErrors: {},
					adding: false,
				};

				this.addEventListener('click', this.onClick.bind(this));
				this.addEventListener('input', this.onInput.bind(this));
				this.addEventListener('keydown', this.onKeydown.bind(this));

				if (this.drawerEl) {
					this.drawerEl.addEventListener(
						'click',
						this.onDrawerClick.bind(this),
					);
				}

				// One call per page, started last so nothing above waits on it, and never
				// awaited: the creator is fully usable before it lands.
				this.loadAvailability();
				window.mtmAvailability = this.reportAvailability.bind(this);
			}

			// R9's readout: `mtmAvailability()` in the console answers "why is this fabric
			// not offered?" — usually from `generated_at`, which is when illumin8 built the
			// file rather than when we served it.
			reportAvailability() {
				return {
					generated_at: this.availabilityGeneratedAt,
					fabrics: this.availability,
					product_fabric_ids: this.productFabricIds(),
					current_fabric_id: this.currentFabricId(),
				};
			}

			// ---------- state ----------

			setState(patch) {
				Object.assign(this.state, patch);
				this.render();
			}

			// The steps that apply to the CURRENT selection. Curtains only show a control
			// step once a motorisable heading is chosen; everything else is fixed per flow.
			steps() {
				var self = this;
				return this.flow.steps.filter(function (step) {
					if (step !== 'control' || self.flow.key !== 'curtain') return true;
					var heading = self.currentHeading();
					return !!(heading && heading.motorisable);
				});
			}

			stepIndex(step) {
				return this.steps().indexOf(step);
			}

			// The step after `step`, skipping any that do not apply.
			nextStep(step) {
				var steps = this.steps();
				return steps[steps.indexOf(step) + 1] || null;
			}

			// The step before `step`, so a Previous button names whatever the current flow
			// actually has in front of it rather than a step hard-coded per panel.
			prevStep(step) {
				var steps = this.steps();
				var index = steps.indexOf(step);
				return (index > 0 && steps[index - 1]) || null;
			}

			goToStep(step) {
				var index = this.stepIndex(step);
				this.state.step = step;
				if (index > this.state.furthest) this.state.furthest = index;
				this.render();
				this.scrollStepIntoView(step);

				// These tiles quote whole-item prices, so their step needs the prices up
				// front rather than on the way to the summary.
				if (step === 'lining') this.ensureQuotePrices([null]);
				if (step === 'cloth') {
					var self = this;
					var clothIds = this.config.clothFabricIds || {};
					this.ensureQuotePrices(
						this.availableCloths()
							// Two middleware calls each, and its tile shows no price anyway.
							.filter(function (cloth) {
								return !self.fabricUnavailable(Number(clothIds[cloth.key]));
							})
							.map(function (cloth) {
								return { cloth: cloth.key };
							}),
					);
				}
			}

			// Fetch what a step's tiles need to quote whole-item prices, so they have them
			// before the summary asks for one. Each entry of `variants` is a selectedChoices()
			// override describing one tile's configuration — null for a step whose tiles all
			// price against the current one. A base cloth is a different fabric, so its tiles
			// each need a fabric price AND an option menu of their own; a lining reuses both.
			//
			// Quiet by design: a tile shows no figure until its price lands, and a failure is
			// left to the step the shopper is trying to leave, which retries it and can
			// report it to someone who is actually waiting on a price.
			ensureQuotePrices(variants) {
				var self = this;
				var step = this.state.step;
				var blindtypeId = this.currentBlindtypeId();
				var measureId = this.currentMeasureId();

				variants.forEach(function (overrides) {
					var work = [];

					if (!self.optionsFor(blindtypeId, overrides)) {
						work.push(
							self.once(
								'options|' + self.optionKey(blindtypeId, overrides),
								function () {
									return self.loadOptions(blindtypeId, overrides);
								},
							),
						);
					}

					var fabricKey = self.fabricKey(blindtypeId, measureId, overrides);
					if (self.fabricCache[fabricKey] == null) {
						work.push(
							self.once('fabric|' + fabricKey, function () {
								return self.loadFabricPrice(blindtypeId, measureId, overrides);
							}),
						);
					}

					if (!work.length) return;

					Promise.all(work).then(
						function () {
							// Only the step that asked for it needs redrawing, and re-rendering
							// a step the shopper has since gone back to would interrupt them
							// mid-answer.
							if (self.state.step === step) self.render();
						},
						function () {},
					);
				});
			}

			// Run `job` at most once per key while it is in flight, so the render each
			// completed fetch triggers cannot start the fetches beside it over again.
			once(key, job) {
				var self = this;
				if (this.pending[key]) return this.pending[key];

				var promise = job();
				this.pending[key] = promise;

				// Cleared only if it is still this job's promise: stepping back and changing
				// the heading starts a fetch under a different key, and that is the one
				// still worth de-duplicating.
				function settle() {
					if (self.pending[key] === promise) delete self.pending[key];
				}
				promise.then(settle, settle);

				return promise;
			}

			// How much of the top of the viewport is covered by sticky/fixed chrome — the
			// site header in both themes, which would otherwise sit over the step we just
			// scrolled to. Asked of the DOM rather than hard-coded: the two themes use
			// different header elements and both change height between breakpoints.
			topObstructionHeight() {
				if (!document.elementsFromPoint) return 0;

				var stack = document.elementsFromPoint(
					Math.round(window.innerWidth / 2),
					1,
				);
				var limit = window.innerHeight * 0.4; // a site header is never taller than this
				var bottom = 0;

				for (var i = 0; i < stack.length; i++) {
					var el = stack[i];
					if (el === document.body || el === document.documentElement) break;
					if (this.contains(el)) continue; // our own markup is not chrome

					var position = window.getComputedStyle(el).position;
					if (position !== 'fixed' && position !== 'sticky') continue;

					var rect = el.getBoundingClientRect();
					if (rect.height <= limit && rect.bottom > bottom)
						bottom = rect.bottom;
				}

				return bottom;
			}

			// Land the top of the step just below the site header. This used to centre the
			// header instead, which left the step floating mid-screen under a screenful of
			// the steps already completed.
			scrollStepIntoView(step) {
				var header = this.querySelector(
					'[data-mtm-step-header="' + step + '"]',
				);
				if (!header) return;

				var GAP = 12;
				var target =
					window.scrollY +
					header.getBoundingClientRect().top -
					this.topObstructionHeight() -
					GAP;

				window.scrollTo({
					top: Math.max(0, target),
					behavior: window.matchMedia('(prefers-reduced-motion: reduce)')
						.matches
						? 'auto'
						: 'smooth',
				});
			}

			// ---------- derived selection ----------

			currentHeading() {
				return this.state.heading
					? findBy(this.flow.headings, 'key', this.state.heading)
					: null;
			}

			currentBlindtypeId() {
				var heading = this.currentHeading();
				return heading ? heading.blindtypeId : this.flow.defaultBlindtypeId;
			}

			// Curtains take the datum from the heading (eyelet is measured from the top of
			// the pole, pleats from the underside of the ring). Blinds take it from the
			// Fitting Type the shopper picked.
			currentMeasureId() {
				if (this.flow.key !== 'curtain') {
					var fitting = findBy(FITTINGS, 'key', this.state.fitting);
					return fitting ? fitting.measureId : this.flow.defaultMeasureId;
				}
				var heading = this.currentHeading();
				return heading ? heading.measureId : this.flow.defaultMeasureId;
			}

			// Roller base cloth is fabric identity, not an option, so it changes the
			// fabricid every price call is made against. `overrides` asks the same
			// question of a cloth the shopper has not picked, so the base cloth tiles can
			// each quote what the blind would cost on that cloth.
			currentFabricId(overrides) {
				if (this.flow.key === 'roller') {
					var state = overrides
						? Object.assign({}, this.state, overrides)
						: this.state;
					var cloths = this.config.clothFabricIds || {};
					var id = state.cloth ? cloths[state.cloth] : null;
					if (id) return Number(id);
					// Before a cloth is chosen, quote against the first one the product offers
					// so the confirm-size step can show a base price — but skip a cloth
					// illumin8 have deactivated, or the base price would be quoted against a
					// fabric the shopper is about to be told they cannot have.
					var self = this;
					var available = this.availableCloths();
					var supplied = available.filter(function (cloth) {
						return !self.fabricUnavailable(Number(cloths[cloth.key]));
					});
					var preferred = supplied.length ? supplied : available;
					return preferred.length ? Number(cloths[preferred[0].key]) : 0;
				}
				return Number(this.config.fabricId);
			}

			availableCloths() {
				var cloths = this.config.clothFabricIds || {};
				return BASE_CLOTHS.filter(function (cloth) {
					return !!cloths[cloth.key];
				});
			}

			motorised() {
				var heading = this.currentHeading();
				if (!heading) return false;
				if (this.flow.key === 'curtain')
					return (
						!!heading.motorisable && this.state.controlType === 'motorised'
					);
				return heading.control === 'motorised';
			}

			// The motor a heading takes. Narrow blinds take a different unit — "to be used
			// for blinds from 560mm to 650mm wide" (workbook, row 15).
			motorCodeFor(heading) {
				if (!heading || !heading.motor) return null;
				if (this.flow.key === 'curtain') return heading.motor;

				var widthMm = this.state.widthMm || 0;
				if (widthMm >= NARROW_MOTOR.minMm && widthMm <= NARROW_MOTOR.maxMm)
					return NARROW_MOTOR.code;
				return heading.motor;
			}

			// The motor for the current configuration, or null while it is manual.
			currentMotorCode() {
				if (!this.motorised()) return null;
				return this.motorCodeFor(this.currentHeading());
			}

			// The track for the current curtain heading, if it has one.
			currentTrackCode() {
				var heading = this.currentHeading();
				if (!heading || this.flow.key !== 'curtain') return null;
				if (this.motorised()) return heading.motorTrack || null;
				return heading.manualTrack || null;
			}

			// The option groups the UI asks about for the current selection. Anything else
			// is defaulted — cheapest for curtains, BLIND_DEFAULTS for the blind ranges.
			coveredGroupIds() {
				var heading = this.currentHeading();
				var covered = [this.flow.headingGroup];

				if (this.flow.key === 'curtain') {
					covered.push(GROUP.PANEL, GROUP.LINING, GROUP.ADDITIONAL_LINING);
					if (heading && heading.extra === 'eyelet') covered.push(GROUP.EYELET);
					if (heading && (heading.manualTrack || heading.motorTrack))
						covered.push(GROUP.TRACK);
					if (this.motorised())
						covered.push(GROUP.MOTOR, GROUP.MOTOR_REMOTE, GROUP.CHARGER);
					return covered;
				}

				covered.push(GROUP.CONTROL_SIDE);
				if (this.flow.key === 'roman') covered.push(GROUP.LINING, GROUP.STYLE);
				if (this.motorised()) {
					covered.push(
						GROUP.MOTOR,
						GROUP.MOTOR_REMOTE,
						GROUP.CHARGER,
						GROUP.HUB,
					);
				} else {
					covered.push(GROUP.CHAIN);
				}
				return covered;
			}

			// ---------- middleware calls ----------

			// The middleware base URL is a section setting and is easy to leave blank. If it
			// is, every request resolves against the shop domain and 404s, which would
			// otherwise surface as "that combination is not available" and send someone
			// hunting for a pricing bug that does not exist.
			middlewareMissing() {
				return this.base
					? null
					: new Error(
							'Made-to-measure pricing is not set up on this store yet. Please contact us.',
						);
			}

			// ---------- fabric availability ----------
			//
			// illumin8 deactivate a cloth when they cannot supply it, and the middleware
			// publishes the current list at /mtm/availability. What follows is PRESENTATION
			// ONLY: /mtm/price refuses to sign a quote for a deactivated fabric whatever the
			// browser believes, so the worst a failure here can cost is a shopper configuring
			// something and being turned down at the end — the behaviour before any of this.
			// It cannot let an unbuildable order through.
			//
			// Fetched once when the creator mounts and held in memory for the page. Never
			// stored: supply changes through the day, and a shopper returning tomorrow to
			// yesterday's cached answer is the exact staleness this is meant to prevent. The
			// response is cacheable for five minutes, which is what stops a second page view
			// asking again.
			loadAvailability() {
				var self = this;
				if (this.middlewareMissing()) return;

				// Deliberately not awaited by anything. The creator renders and is fully
				// usable while this is in flight; the answer is applied when it lands.
				getJSON(this.base + '/mtm/availability', 8000)
					.then(function (payload) {
						var fabrics = payload && payload.fabrics;
						if (!fabrics || typeof fabrics !== 'object') {
							throw new Error('no fabrics map in the payload');
						}

						self.availability = fabrics;
						self.availabilityGeneratedAt = payload.generated_at || null;

						debugLine(
							'availability for ' +
								Object.keys(fabrics).length +
								' fabrics, generated ' +
								(self.availabilityGeneratedAt || 'never'),
						);

						// Typing in the size fields deliberately does not re-render, so only
						// redraw when the answer actually changes what is on screen — which on
						// the overwhelming majority of products it does not.
						if (self.anyFabricUnavailable()) self.render();
					})
					.catch(function (error) {
						// Fail open, and quietly. Every fabric stays selectable and /mtm/price
						// is left to refuse the ones illumin8 cannot make. One line and no
						// retry: nothing here is worth a banner, and a retry loop against a
						// five-minute-cacheable endpoint would outnumber the price calls the
						// shopper is actually waiting on.
						console.warn(
							'[mtm] could not read fabric availability, offering every fabric:',
							error.message,
						);
					});
			}

			// What illumin8 say about one fabric id, or null when we have no opinion.
			//
			// A fabric the payload does not mention reads as AVAILABLE. An empty or partial
			// response would otherwise take the whole range off the site, which is a far worse
			// failure than offering one cloth too many. The middleware is deliberately the
			// other way round — it refuses to price a fabric it has never been told about —
			// so such a fabric renders normally here and is turned down at the quote, which
			// quoteRefusalMessage explains.
			fabricUnavailable(fabricId) {
				if (!this.availability || !fabricId) return null;

				// JSON object keys are strings; fabric ids are numbers everywhere else.
				var row = this.availability[String(fabricId)];
				if (!row || row.available !== false) return null;

				return { eta: row.eta || null };
			}

			// The sentence a shopper is shown about an unavailable fabric. An ETA is copy and
			// nothing more — a cloth with a date is exactly as unsellable as one without, so
			// neither wording offers to take the order now and ship it later.
			unavailableReason(fabricId) {
				var row = this.fabricUnavailable(fabricId);
				if (!row) return null;

				var eta = formatEta(row.eta);
				return eta ? 'Back in stock ' + eta : 'Currently unavailable';
			}

			// Every fabric id this product could be made from: one for curtains and Romans,
			// one per base cloth for a roller.
			productFabricIds() {
				if (this.flow.key !== 'roller') {
					return [Number(this.config.fabricId)].filter(Boolean);
				}
				var cloths = this.config.clothFabricIds || {};
				return this.availableCloths()
					.map(function (cloth) {
						return Number(cloths[cloth.key]);
					})
					.filter(Boolean);
			}

			anyFabricUnavailable() {
				var self = this;
				return this.productFabricIds().some(function (fabricId) {
					return !!self.fabricUnavailable(fabricId);
				});
			}

			// Nothing on this page can be made. For a curtain or a Roman that is the single
			// fabric; for a roller it means both base cloths are out, since one cloth being
			// available still leaves a blind to configure.
			nothingAvailable() {
				var self = this;
				var ids = this.productFabricIds();
				return (
					ids.length > 0 &&
					ids.every(function (fabricId) {
						return !!self.fabricUnavailable(fabricId);
					})
				);
			}

			// fabricId is part of the key: the roller base cloth switches fabric mid-session,
			// and without it the second cloth would read the first cloth's cached menu.
			optionKey(blindtypeId, overrides) {
				return (
					blindtypeId +
					'|' +
					this.currentFabricId(overrides) +
					'|' +
					this.state.widthMm +
					'|' +
					this.state.dropMm
				);
			}

			loadOptions(blindtypeId, overrides) {
				var missing = this.middlewareMissing();
				if (missing) return Promise.reject(missing);

				var key = this.optionKey(blindtypeId, overrides);
				if (this.optionCache[key]) {
					debugLine('options served from cache ' + key);
					return Promise.resolve(this.optionCache[key]);
				}

				var params = new URLSearchParams({
					blindtypeid: blindtypeId,
					fabricid: this.currentFabricId(overrides),
					width_mm: this.state.widthMm,
					drop_mm: this.state.dropMm,
				});

				var self = this;
				// A little longer than the default: the middleware retries a failed menu
				// before giving up, so this request can outlast a plain proxy call.
				return getJSON(this.base + '/mtm/options?' + params, 20000).then(
					function (rows) {
						var groups = groupOptions(rows, blindtypeId);
						// Nothing cacheable in it. Caching an unusable menu would pin the
						// failure to this fabric and size for the rest of the session, since
						// every later lookup is served from the cache without asking again.
						if (!Object.keys(groups).length) {
							throw new Error(
								'The pricing service is temporarily unavailable. Please try again shortly.',
							);
						}
						debugGroups(key, groups);
						self.optionCache[key] = groups;
						return groups;
					},
				);
			}

			fabricKey(blindtypeId, measureId, overrides) {
				return (
					blindtypeId +
					'|' +
					measureId +
					'|' +
					this.currentFabricId(overrides) +
					'|' +
					this.state.widthMm +
					'|' +
					this.state.dropMm
				);
			}

			loadFabricPrice(blindtypeId, measureId, overrides) {
				var missing = this.middlewareMissing();
				if (missing) return Promise.reject(missing);

				var key = this.fabricKey(blindtypeId, measureId, overrides);
				if (this.fabricCache[key] != null) {
					debugLine(
						'fabric price served from cache ' +
							key +
							' = ' +
							this.fabricCache[key],
					);
					return Promise.resolve(this.fabricCache[key]);
				}

				var params = new URLSearchParams({
					blindtypeid: blindtypeId,
					fabricid: this.currentFabricId(overrides),
					measureid: measureId,
					width_mm: this.state.widthMm,
					drop_mm: this.state.dropMm,
				});

				var self = this;
				return getJSON(this.base + '/mtm/fabric-price?' + params).then(
					function (rows) {
						if (!Array.isArray(rows) || rows.length === 0) {
							throw new Error(
								'No price is available for this fabric at those measurements.',
							);
						}
						var row = rows[0];
						var price = Number(
							row.SellPrice != null ? row.SellPrice : row.TotalSalesAmt,
						);
						if (!isFinite(price)) {
							throw new Error(
								'Unable to read the fabric price. Please try again.',
							);
						}
						debugLine('fabric price ' + key + ' = ' + price);
						self.fabricCache[key] = price;
						return price;
					},
				);
			}

			optionsFor(blindtypeId, overrides) {
				return this.optionCache[this.optionKey(blindtypeId, overrides)] || null;
			}

			// Load the menu for every blind type the current flow can reach, if it is not
			// already cached under the current fabric and size. Silent: a blind type with
			// no menu simply leaves its tiles disabled.
			ensureOptions() {
				var self = this;
				var ids = this.headingBlindtypeIds().filter(function (blindtypeId) {
					return !self.optionsFor(blindtypeId);
				});
				if (!ids.length) return Promise.resolve();

				this.setState({ loading: true });
				return Promise.all(
					ids.map(function (blindtypeId) {
						return self.loadOptions(blindtypeId).catch(function () {
							return null;
						});
					}),
				).then(function () {
					self.setState({ loading: false });
				});
			}

			// The distinct blind types the current flow's heading tiles span — four for
			// curtains, two for each blind flow.
			headingBlindtypeIds() {
				var ids = [];
				this.flow.headings.forEach(function (heading) {
					if (ids.indexOf(heading.blindtypeId) === -1) {
						ids.push(heading.blindtypeId);
					}
				});
				return ids;
			}

			// Resolve a design-level ChoiceCode to the live illumin8 row (id + current price).
			choiceByCode(blindtypeId, groupId, code) {
				var groups = this.optionsFor(blindtypeId);
				if (!groups || !groups[groupId]) return null;
				var choices = groups[groupId].choices;
				for (var i = 0; i < choices.length; i++) {
					if (choices[i].code === code) return choices[i];
				}
				return null;
			}

			// What a heading tile quotes as its add-on. A motorised headrail is the headrail
			// AND the motor that drives it: illumin8 price those as two options, but nobody
			// weighing "Cassette" against "Cassette Motorised" can be shown only the cassette
			// part of the difference. Curtains ask about motorisation at a step of its own,
			// where the motor is priced, so their tiles stay the heading alone.
			headingPrice(heading) {
				var choice = this.choiceByCode(
					heading.blindtypeId,
					this.flow.headingGroup,
					heading.finish,
				);
				if (!choice) return null;
				if (this.flow.key === 'curtain' || heading.control !== 'motorised')
					return choice.price;

				var motorCode = this.motorCodeFor(heading);
				var motor = motorCode
					? this.choiceByCode(heading.blindtypeId, GROUP.MOTOR, motorCode)
					: null;
				// A missing motor row means the figure would understate the headrail, not
				// that the motor is free. Quote nothing rather than quote too little.
				if (!motor) return null;
				return (choice.price || 0) + (motor.price || 0);
			}

			// ---------- price assembly ----------

			// Every choice that makes up the current configuration: the ones the shopper
			// picked, plus a default for each group the UI does not expose.
			//
			// `overrides` answers "what would this cost with that lining instead?" without
			// touching the real selection, so the lining and base cloth tiles can each
			// quote a total. Overridable: lining and its colour, which are read here and
			// nowhere else, and the roller base cloth, which the fabric-keyed caches below
			// follow through currentFabricId(). Heading and size are not — they feed the
			// blind type and measure, which every derivation here reads off this.state.
			//
			// Throws when a blind range meets a group it has no reviewed default for —
			// better a visible error than an order for the wrong physical blind.
			selectedChoices(overrides) {
				var self = this;
				var state = overrides
					? Object.assign({}, this.state, overrides)
					: this.state;
				var blindtypeId = this.currentBlindtypeId();
				// The base cloth is a different fabric, and illumin8 price options per
				// fabric, so an overridden cloth has a menu of its own to price against.
				var groups = this.optionsFor(blindtypeId, overrides);
				if (!groups) return null;

				var heading = this.currentHeading();
				var chosen = [];
				var covered = this.coveredGroupIds();

				// Resolved against `groups` rather than through choiceByCode(), which would
				// go back to the menu of the cloth actually selected.
				function findChoice(group, code) {
					for (var i = 0; i < group.choices.length; i++) {
						if (group.choices[i].code === code) return group.choices[i];
					}
					return null;
				}

				function push(groupId, code) {
					if (!code) return;
					var group = groups[groupId];
					if (!group) return;
					var choice = findChoice(group, code);
					if (choice) chosen.push({ group: group, choice: choice });
				}

				// Heading — the Finish choice on curtains and rollers, System on Roman.
				if (heading) push(this.flow.headingGroup, heading.finish);

				if (this.flow.key === 'curtain') {
					var panel = findBy(PANELS, 'key', this.state.panel);
					if (panel) push(GROUP.PANEL, panel.code);

					var lining = findBy(LININGS, 'key', state.lining);
					if (lining && state.liningColour)
						push(GROUP.LINING, lining.colours[state.liningColour]);

					var interlining = findBy(INTERLINING, 'key', this.state.interlining);
					if (interlining) push(GROUP.ADDITIONAL_LINING, interlining.code);

					if (
						heading &&
						heading.extra === 'eyelet' &&
						this.state.eyeletColour
					) {
						var eyelet = findBy(EYELET_COLOURS, 'key', this.state.eyeletColour);
						if (eyelet) push(GROUP.EYELET, eyelet.code);
					}
				} else if (this.flow.key === 'roman') {
					var romanLining = findBy(ROMAN_LININGS, 'key', state.lining);
					if (romanLining && state.liningColour)
						push(GROUP.LINING, romanLining.colours[state.liningColour]);

					var romanStyle = findBy(ROMAN_STYLES, 'key', state.style);
					if (romanStyle) push(GROUP.STYLE, romanStyle.code);
				}

				// Track — the heading's own track when manual, the motorised track otherwise.
				push(GROUP.TRACK, this.currentTrackCode());

				// Control type.
				if (this.flow.key !== 'curtain' || heading) {
					if (this.motorised()) {
						push(GROUP.MOTOR, this.currentMotorCode());

						var remote = findBy(REMOTES, 'key', this.state.remote);
						if (remote) push(GROUP.MOTOR_REMOTE, remote.code);

						var charger = findBy(CHARGERS, 'key', this.state.charger);
						if (charger) push(GROUP.CHARGER, charger.code);

						var hub = findBy(HUBS, 'key', this.state.hub);
						if (hub) push(GROUP.HUB, hub.code);
					} else if (this.flow.key !== 'curtain') {
						var chain = findBy(CHAINS, 'key', this.state.chain);
						if (chain) push(GROUP.CHAIN, chain.code);
					} else if (heading.motorisable) {
						// A motorisable curtain left manual still has to say so.
						push(GROUP.MOTOR, 'NOMOTOR');
					}
				}

				if (this.flow.key !== 'curtain' && this.state.controlSide) {
					var side = findBy(CONTROL_SIDES, 'key', this.state.controlSide);
					if (side) {
						var group = groups[GROUP.CONTROL_SIDE];
						if (group) {
							side.codes.some(function (code) {
								var match = findChoice(group, code);
								if (match) chosen.push({ group: group, choice: match });
								return !!match;
							});
						}
					}
				}

				var defaults = this.flow.defaults && this.flow.defaults[blindtypeId];

				Object.keys(groups).forEach(function (groupId) {
					var id = Number(groupId);
					if (covered.indexOf(id) !== -1) return;
					var group = groups[groupId];

					// Curtains: cheapest, as before. Few groups, all low-stakes.
					if (!self.flow.defaults) {
						var cheapest = group.choices[0];
						group.choices.forEach(function (choice) {
							if (choice.price < cheapest.price) cheapest = choice;
						});
						if (cheapest) chosen.push({ group: group, choice: cheapest });
						return;
					}

					// Blinds: an explicit, reviewed choice or nothing at all.
					var wanted = defaults && defaults[id];
					if (wanted && typeof wanted === 'object') {
						wanted =
							wanted[self.state.heading] != null
								? wanted[self.state.heading]
								: wanted._;
					}

					if (!wanted) {
						throw new Error(
							'This blind has an option we cannot configure yet (' +
								(group.groupName || 'option ' + id) +
								'). Please contact us and we will complete your order.',
						);
					}

					var choice = findChoice(group, wanted);
					if (!choice) {
						throw new Error(
							'One of this blind’s standard options is unavailable right now (' +
								(group.groupName || 'option ' + id) +
								'). Please try again shortly.',
						);
					}
					chosen.push({ group: group, choice: choice });
				});

				return chosen;
			}

			// Configured total, exactly as illumin8 priced it. Needs the fabric price and option
			// menu already cached.
			// Propagates the error selectedChoices() throws for an unconfigurable group.
			computeTotal(overrides) {
				var blindtypeId = this.currentBlindtypeId();
				var fabricPrice =
					this.fabricCache[
						this.fabricKey(blindtypeId, this.currentMeasureId(), overrides)
					];
				if (fabricPrice == null) return null;

				var chosen = this.selectedChoices(overrides);
				if (!chosen) return null;

				return chosen.reduce(function (total, entry) {
					return total + (entry.choice.price || 0);
				}, fabricPrice);
			}

			// computeTotal() for a tile: null rather than a thrown error when the
			// configuration cannot be priced yet. A tile that cannot quote a total simply
			// shows no figure — the step the shopper is actually trying to leave reports
			// the failure, and this must never take the panel down with it.
			quoteTotal(overrides) {
				try {
					return this.computeTotal(overrides);
				} catch (error) {
					return null;
				}
			}

			// ---------- validation ----------

			sizeErrors() {
				var errors = {};
				var limits = this.limits;
				var width = this.state.widthMm;
				var drop = this.state.dropMm;

				if (width == null || isNaN(width)) {
					errors.width = 'Please enter a width.';
				} else if (width < limits.widthMin || width > limits.widthMax) {
					errors.width =
						'Width must be between ' +
						this.formatBound(limits.widthMin, 'min') +
						' and ' +
						this.formatBound(limits.widthMax, 'max') +
						'.';
				}

				if (drop == null || isNaN(drop)) {
					errors.drop = 'Please enter a drop.';
				} else if (drop < limits.dropMin || drop > limits.dropMax) {
					errors.drop =
						'Drop must be between ' +
						this.formatBound(limits.dropMin, 'min') +
						' and ' +
						this.formatBound(limits.dropMax, 'max') +
						'.';
				}

				return errors;
			}

			// A length in the unit currently selected.
			length(mm) {
				return formatLength(mm, this.state.unit);
			}

			// A limit as the shopper can actually enter it. A limit rarely lands on an
			// eighth, so in inches it is quoted snapped INWARDS — a stated minimum that is
			// itself rejected on entry would be worse than no minimum at all.
			formatBound(mm, side) {
				if (this.state.unit !== 'in') return this.length(mm);
				var parts = snapMmToEighths(
					mm,
					side === 'min' ? mm : null,
					side === 'max' ? mm : null,
				);
				return inchesLabel(parts) + ' in';
			}

			// A millimetre value moved onto the nearest eighth of an inch, so that what the
			// two inch controls show and what is priced are the same number rather than a
			// display rounded off a different hidden figure. A value already outside its
			// limits snaps unguarded and stays invalid, rather than being nudged into range.
			snapToEnterable(mm, field) {
				if (mm == null || isNaN(mm)) return mm;
				var min = this.limits[field + 'Min'];
				var max = this.limits[field + 'Max'];
				var inRange = mm >= min && mm <= max;
				var parts = snapMmToEighths(
					mm,
					inRange ? min : null,
					inRange ? max : null,
				);
				return mmFromInches(parts.whole, parts.eighths);
			}

			sizeComplete() {
				if (Object.keys(this.sizeErrors()).length) return false;
				// Fitting Type is asked alongside the measurements on the blind ranges.
				if (this.flow.key !== 'curtain' && !this.state.fitting) return false;
				return true;
			}

			// An entered measurement in millimetres, which is the unit the heading limits
			// use.
			enteredMm(field) {
				var mm = field === 'width' ? this.state.widthMm : this.state.dropMm;
				return mm == null || isNaN(mm) ? null : mm;
			}

			// Whether a heading can be made at the measurement already entered for one
			// dimension. A heading that states no limit on it always fits.
			headingFitsDimension(heading, dimension) {
				var limit = heading && heading[dimension.key];
				var mm = this.enteredMm(dimension.field);
				if (!limit || mm == null) return true;
				return mm >= limit.min && mm <= limit.max;
			}

			// The dimensions a heading cannot be made at, so a caller can both test whether
			// it fits (an empty list does) and say which measurement is the problem.
			headingMisfits(heading) {
				var self = this;
				return DIMENSIONS.filter(function (dimension) {
					return !self.headingFitsDimension(heading, dimension);
				});
			}

			headingFitsSize(heading) {
				return !this.headingMisfits(heading).length;
			}

			// Whether a heading states a size limit of its own at all.
			headingHasLimits(heading) {
				return DIMENSIONS.some(function (dimension) {
					return !!(heading && heading[dimension.key]);
				});
			}

			// The size a heading can be ordered at, for its tile to state up front — but
			// only where the heading is the thing doing the limiting. A heading that states
			// no size of its own is made to whatever the product allows, and the size step
			// has already enforced that, so repeating it on the tile would read as a
			// restriction where there is none.
			//
			// Where a heading is limited, its own figure is narrowed by the product's,
			// because the size step enforces that one as well — quoting illumin8's alone
			// would advertise a size the shopper cannot get past validation. A dimension
			// that heading says nothing about is quoted at the product's own limit, so the
			// tile still states all four. Nothing is stated unless every bound is known.
			headingLimitLines(heading) {
				var self = this;
				var limits = this.limits;
				var bounds = [];

				if (!this.headingHasLimits(heading)) return [];

				DIMENSIONS.forEach(function (dimension) {
					var own = (heading && heading[dimension.key]) || {};
					var productMin = limits[dimension.field + 'Min'];
					var productMax = limits[dimension.field + 'Max'];
					bounds.push([
						'Min ' + dimension.label,
						own.min == null ? productMin : Math.max(own.min, productMin),
						'min',
					]);
					bounds.push([
						'Max ' + dimension.label,
						own.max == null ? productMax : Math.min(own.max, productMax),
						'max',
					]);
				});

				if (
					bounds.some(function (bound) {
						return bound[1] == null || isNaN(bound[1]);
					})
				)
					return [];

				return bounds.map(function (bound) {
					return bound[0] + ': ' + self.formatBound(bound[1], bound[2]);
				});
			}

			headingComplete() {
				var heading = this.currentHeading();
				if (!heading) return false;
				// The size was entered before this step, so it is checked here instead.
				if (!this.headingFitsSize(heading)) return false;
				if (heading.extra === 'eyelet' && !this.state.eyeletColour)
					return false;
				return true;
			}

			// Which control fields are still unanswered — drives the red labels in the design.
			controlMissing() {
				var missing = [];
				if (this.flow.key === 'curtain') {
					if (!this.state.controlType) missing.push('controlType');
					if (this.motorised() && !this.state.remote) missing.push('remote');
					return missing;
				}
				if (this.motorised()) {
					if (!this.state.remote) missing.push('remote');
					if (!this.state.charger) missing.push('charger');
					if (!this.state.hub) missing.push('hub');
				} else if (!this.state.chain) {
					missing.push('chain');
				}
				if (!this.state.controlSide) missing.push('controlSide');
				return missing;
			}

			// The fold style — Roman only, and one field, but it is gated like the rest.
			styleMissing() {
				return this.state.style ? [] : ['style'];
			}

			// Which lining groups are still unanswered — drives the red labels in the design.
			liningMissing() {
				var missing = [];
				if (!this.state.lining) missing.push('lining');
				if (!this.state.liningColour) missing.push('liningColour');
				if (this.flow.key === 'curtain' && !this.state.interlining)
					missing.push('interlining');
				return missing;
			}

			clothMissing() {
				return this.state.cloth ? [] : ['cloth'];
			}

			// The gate on the step that immediately precedes the summary.
			lastStepMissing() {
				if (this.flow.key === 'roller') return this.clothMissing();
				return this.liningMissing();
			}

			// ============================================================
			// Rendering
			// ============================================================

			render() {
				// Nothing on this page is buildable. The Buy buttons are already suppressed
				// on a made-to-measure product, so leaving a live "Create your curtains"
				// would walk the shopper through a configuration only to refuse it at the
				// end. The notice takes the button's place and says when, if illumin8 have
				// told us. Ordering a sample still works, and is the one thing left worth
				// doing here — hence no second call to action.
				this.renderUnavailableNotice();

				// Only before they start. A shopper part-way through has answered six
				// questions, and tearing the accordion out from under them on a late refusal
				// would leave the notice reading as though the page had always said it —
				// with no sign of what just happened and no way back to what they chose.
				// Mid-flow, the notice sits above the steps, the refusal is shown on the
				// step itself and Add to bag stays disabled.
				if (!this.state.started) {
					this.startEl.hidden = this.nothingAvailable();
					this.stepsEl.hidden = true;
					this.stepsEl.innerHTML = '';
					return;
				}

				// The sample link is never hidden — it stays available while configuring.
				this.startEl.hidden = true;
				this.stepsEl.hidden = false;

				var self = this;
				var steps = this.steps();

				// 'confirm' is a checkpoint rather than a section of the accordion: it is
				// listed only while it is the open step, exactly as the designs show.
				var visible = steps.filter(function (step) {
					if (step === 'confirm') return self.state.step === 'confirm';
					if (step === 'summary')
						return self.state.furthest >= self.stepIndex('summary');
					return true;
				});

				this.stepsEl.innerHTML = visible
					.map(function (step) {
						return self.renderStep(step);
					})
					.join('');
			}

			// The banner that stands in for the start button, and stays on screen while a
			// roller is configured on an unavailable cloth. `role="status"` is on the
			// element in the Liquid, so a shopper already on the page when this lands hears
			// it without the announcement interrupting them.
			renderUnavailableNotice() {
				if (!this.unavailableEl) return;

				var reason = this.unavailableReason(this.currentFabricId());
				if (!reason) {
					this.unavailableEl.hidden = true;
					this.unavailableEl.innerHTML = '';
					return;
				}

				var body = this.nothingAvailable()
					? 'We are waiting on this fabric from our maker, so it cannot be made ' +
						'to measure at the moment. You can still order a sample.'
					: 'This base cloth cannot be made at the moment. Choose the other cloth ' +
						'to carry on.';

				this.unavailableEl.innerHTML =
					'<div class="mtm-callout">' +
					icon('mtm-i-info', 'mtm-callout__icon') +
					'<span>' +
					'<strong class="mtm-unavailable__reason">' +
					escapeHtml(reason) +
					'</strong>' +
					escapeHtml(body) +
					'</span>' +
					'</div>';
				this.unavailableEl.hidden = false;
			}

			renderStep(step) {
				var isOpen = this.state.step === step;
				var reachable = this.stepIndex(step) <= this.state.furthest;

				var html =
					'<div class="mtm-step" data-mtm-step="' +
					step +
					'">' +
					'<button type="button" class="mtm-step__header" data-mtm-step-header="' +
					step +
					'"' +
					' aria-expanded="' +
					(isOpen ? 'true' : 'false') +
					'"' +
					' aria-controls="mtm-panel-' +
					step +
					'"' +
					(reachable ? '' : ' disabled') +
					'>' +
					'<span>' +
					escapeHtml(this.flow.stepLabels[step]) +
					'</span>' +
					icon('mtm-i-chevron', 'mtm-step__chevron') +
					'</button>';

				if (isOpen) {
					html +=
						'<div class="mtm-step__panel" id="mtm-panel-' +
						step +
						'" role="region"' +
						' aria-label="' +
						escapeHtml(this.flow.stepLabels[step]) +
						'">' +
						this.renderPanel(step) +
						'</div>';
				}

				return html + '</div>';
			}

			renderPanel(step) {
				switch (step) {
					case 'size':
						return this.renderSizePanel();
					case 'confirm':
						return this.renderConfirmPanel();
					case 'heading':
						return this.renderHeadingPanel();
					case 'control':
						return this.renderControlPanel();
					case 'style':
						return this.renderStylePanel();
					case 'lining':
						return this.renderLiningPanel();
					case 'cloth':
						return this.renderClothPanel();
					case 'summary':
						return this.renderSummaryPanel();
					default:
						return '';
				}
			}

			// ---------- step 1: size ----------

			renderSizePanel() {
				var self = this;
				var isCurtain = this.flow.key === 'curtain';

				// Curtains choose Single/Pair here; blinds choose the Fitting Type.
				var typeSection = isCurtain
					? '<div>' +
						'<span class="mtm-field-label">Curtain type:</span>' +
						'<div class="mtm-tiles mtm-tiles--panel">' +
						PANELS.map(function (panel) {
							return self.tile('panel', panel.key, panel.label, {
								icon: panel.icon,
								selected: self.state.panel === panel.key,
							});
						}).join('') +
						'</div>' +
						'</div>'
					: '<div>' +
						'<span class="mtm-field-label">Fitting type:' +
						(this.state.fitting
							? ''
							: ' <span class="mtm-field-label__hint">Please choose a fitting</span>') +
						'</span>' +
						'<div class="mtm-tiles mtm-tiles--panel">' +
						FITTINGS.map(function (fitting) {
							return self.tile('fitting', fitting.key, fitting.label, {
								icon: fitting.icon,
								selected: self.state.fitting === fitting.key,
							});
						}).join('') +
						'</div>' +
						'<button type="button" class="mtm-info-link" data-mtm-drawer-open="fitting">' +
						icon('mtm-i-info', '') +
						'What is the difference?</button>' +
						'</div>';

				return (
					typeSection +
					'<p class="mtm-prose">Enter the width and drop of your chosen ' +
					escapeHtml(this.flow.noun) +
					'. For help, please refer to the &lsquo;How to Measure&rsquo; guide</p>' +
					'<div class="mtm-callout">' +
					icon('mtm-i-info', 'mtm-callout__icon') +
					'<span>' +
					(isCurtain
						? 'For curtain poles, measure the distance between the two finials the ends of the pole ' +
							'and record this as your width.'
						: 'For a recess fit, measure the width of the opening in three places and give us the ' +
							'smallest. We allow the clearances the blind needs.') +
					'</span>' +
					'</div>' +
					'<button type="button" class="mtm-btn mtm-btn--outline mtm-btn--full" data-mtm-drawer-open="measure">' +
					icon('mtm-i-measure', 'mtm-btn__icon') +
					'How to measure ' +
					escapeHtml(this.flow.noun) +
					'</button>' +
					this.unitField() +
					this.measurementField('width', 'Width') +
					this.measurementField('drop', 'Drop') +
					// Floor-length curtains only: the fabric settles over time, so the drop
					// they give us wants to be 1cm short of the measurement to the floor.
					(isCurtain
						? '<div class="mtm-callout">' +
							icon('mtm-i-info', 'mtm-callout__icon') +
							'<span>' +
							'Measuring for floor length curtains? Take 1cm off your ' +
							'pole&ndash;to&ndash;floor measurement and enter that as your drop. ' +
							'Curtains drop a little naturally over time, and the 1cm keeps ' +
							'them clear of the floor.' +
							'</span>' +
							'</div>'
						: '') +
					this.renderError() +
					'<div class="mtm-actions mtm-actions--single">' +
					'<button type="button" class="mtm-btn mtm-btn--primary" data-mtm-next="size"' +
					(this.sizeComplete() && !this.state.loading ? '' : ' disabled') +
					'>' +
					(this.state.loading ? 'Calculating&hellip;' : 'Next') +
					(this.state.loading
						? ''
						: icon('mtm-i-arrow-right', 'mtm-btn__icon')) +
					'</button>' +
					'</div>'
				);
			}

			// The unit the measurements are entered in. Only ever a display format: the
			// price request, the signature and the purchase order are millimetres whichever
			// of the three is selected.
			unitField() {
				var self = this;
				return (
					'<div class="mtm-input-field">' +
					'<span class="mtm-field-label" id="mtm-unit-label">Enter your measurements in:</span>' +
					'<div class="mtm-units" role="radiogroup" aria-labelledby="mtm-unit-label">' +
					UNITS.map(function (unit) {
						var id = 'mtm-unit-' + unit.key;
						return (
							'<label class="mtm-unit" for="' +
							id +
							'">' +
							'<input class="mtm-unit__input" type="radio" name="mtm-unit"' +
							' id="' +
							id +
							'" value="' +
							unit.key +
							'" data-mtm-unit' +
							(self.state.unit === unit.key ? ' checked' : '') +
							'>' +
							'<span class="mtm-unit__mark" aria-hidden="true"></span>' +
							'<span class="mtm-unit__label">' +
							escapeHtml(unit.label) +
							'</span>' +
							'</label>'
						);
					}).join('') +
					'</div>' +
					'</div>'
				);
			}

			// One measurement. Millimetres and centimetres are a single number; inches are
			// whole inches plus a fraction from a menu, because nobody wants to type an
			// eighth as 0.125. Both halves are read back out of the DOM together, so the
			// pair only ever resolves to one millimetre figure.
			measurementField(field, label) {
				var unit = this.state.unit;
				var error = this.state.fieldErrors[field];
				var mm = field === 'width' ? this.state.widthMm : this.state.dropMm;
				var minMm = this.limits[field + 'Min'];
				var id = 'mtm-' + field;
				var unitName = unit === 'in' ? 'inches' : unit;
				var errorClass = error ? ' mtm-input--error' : '';
				var placeholder = 'From: ' + this.formatBound(minMm, 'min');
				var control;

				if (unit === 'in') {
					// state.widthMm/dropMm are already an exact number of eighths while inches
					// are selected (mmFromInches on entry, snapToEnterable on the switch), so
					// this reads back the value that was chosen rather than approximating it.
					var parts = mm == null || isNaN(mm) ? null : snapMmToEighths(mm);
					control =
						'<div class="mtm-input-group">' +
						'<input class="mtm-input mtm-input--whole' +
						errorClass +
						'" id="' +
						id +
						'" type="number" inputmode="numeric" data-mtm-input="' +
						field +
						'" min="0" step="1" placeholder="' +
						escapeHtml(placeholder) +
						'" value="' +
						(parts ? parts.whole : '') +
						'">' +
						'<select class="mtm-select' +
						errorClass +
						'" data-mtm-fraction="' +
						field +
						'" aria-label="' +
						escapeHtml(label + ' \u2014 fraction of an inch') +
						'">' +
						EIGHTHS.map(function (eighth) {
							return (
								'<option value="' +
								eighth.value +
								'"' +
								(parts && parts.eighths === eighth.value ? ' selected' : '') +
								'>' +
								eighth.label +
								'</option>'
							);
						}).join('') +
						'</select>' +
						'<span class="mtm-input-suffix">in</span>' +
						'</div>';
				} else {
					// Centimetres carry one decimal so that every millimetre illumin8 can be
					// asked for is still expressible; millimetres are whole numbers.
					var value =
						mm == null || isNaN(mm)
							? ''
							: unit === 'cm'
								? Math.round(mm) / 10
								: Math.round(mm);
					control =
						'<input class="mtm-input' +
						errorClass +
						'" id="' +
						id +
						'" type="number" inputmode="decimal" data-mtm-input="' +
						field +
						'" min="0" step="' +
						(unit === 'cm' ? '0.1' : '1') +
						'" placeholder="' +
						escapeHtml(placeholder) +
						'" value="' +
						value +
						'">';
				}

				return (
					'<div class="mtm-input-field">' +
					'<label class="mtm-field-label" for="' +
					id +
					'">' +
					escapeHtml(label + ' (' + unitName + '):') +
					'</label>' +
					control +
					(error
						? '<span class="mtm-input-error">' + escapeHtml(error) + '</span>'
						: '') +
					'</div>'
				);
			}

			// ---------- step 2: confirm size ----------

			renderConfirmPanel() {
				var panel = findBy(PANELS, 'key', this.state.panel);
				var fitting = findBy(FITTINGS, 'key', this.state.fitting);
				var base = this.state.basePrice;

				return (
					'<div class="mtm-rows">' +
					(this.flow.key === 'curtain'
						? this.row('Curtain type', panel ? panel.label : '—')
						: this.row('Fitting type', fitting ? fitting.label : '—')) +
					this.row(
						'Width',
						formatLengthFull(this.state.widthMm, this.state.unit),
					) +
					this.row(
						'Drop',
						formatLengthFull(this.state.dropMm, this.state.unit),
					) +
					this.priceRow('Base Price', base) +
					'</div>' +
					this.renderError() +
					'<div class="mtm-actions">' +
					'<button type="button" class="mtm-btn mtm-btn--outline" data-mtm-back="size">' +
					icon('mtm-i-arrow-left', 'mtm-btn__icon') +
					'Previous</button>' +
					'<button type="button" class="mtm-btn mtm-btn--primary" data-mtm-next="confirm"' +
					(this.state.loading ? ' disabled' : '') +
					'>' +
					(this.state.loading ? 'Loading&hellip;' : 'Confirm') +
					(this.state.loading
						? ''
						: icon('mtm-i-arrow-right', 'mtm-btn__icon')) +
					'</button>' +
					'</div>'
				);
			}

			// ---------- step 3: heading ----------

			renderHeadingPanel() {
				var self = this;

				var tiles = this.flow.headings
					.map(function (heading) {
						var choice = self.choiceByCode(
							heading.blindtypeId,
							self.flow.headingGroup,
							heading.finish,
						);
						// "No Drill option should be unselectable if Exact fitting type is
						// selected" — workbook, Roman/Roller row 16.
						var fittingBlocked =
							heading.requiresFitting &&
							self.state.fitting &&
							self.state.fitting !== heading.requiresFitting;
						var misfits = self.headingMisfits(heading);
						var requiredFitting = fittingBlocked
							? findBy(FITTINGS, 'key', heading.requiresFitting)
							: null;

						return self.tile('heading', heading.key, heading.label, {
							icon: heading.icon || HEADING_ICONS[heading.key],
							meta: self.headingLimitLines(heading),
							selected: self.state.heading === heading.key,
							disabled: !choice || fittingBlocked || !!misfits.length,
							// Both reasons are answers the shopper gave on the size step, so
							// both are worth naming: they can go back and change either. A
							// heading beaten on both measurements is simply too big or too
							// small, so the note names the size rather than both dimensions.
							note: fittingBlocked
								? 'Only with a ' +
									(requiredFitting
										? requiredFitting.label
										: heading.requiresFitting) +
									' fitting'
								: misfits.length === 1
									? 'Not made at your ' + misfits[0].field
									: misfits.length
										? 'Not made at your size'
										: null,
							price: self.headingPrice(heading),
						});
					})
					.join('');

				var html =
					'<p class="mtm-prose">Select the desired ' +
					(this.flow.key === 'curtain' ? 'curtain heading' : 'headrail') +
					' type from the list below</p>' +
					'<button type="button" class="mtm-btn mtm-btn--outline mtm-btn--full" data-mtm-drawer-open="heading">' +
					icon('mtm-i-info', 'mtm-btn__icon') +
					'Heading information</button>' +
					this.field('Heading type', tiles, { tilesClass: 'mtm-tiles--3' });

				var heading = this.currentHeading();

				// illumin8 build each heading between its own two sizes, and the shopper
				// gave us theirs two steps ago — so a tile can be unbuildable, and a
				// heading picked before the size was edited can stop being buildable.
				// Both need saying out loud: the tiles only grey out, which says nothing.
				var misfits = heading ? this.headingMisfits(heading) : [];
				var blocked = this.flow.headings.filter(function (item) {
					return self.headingHasLimits(item) && !self.headingFitsSize(item);
				});

				if (misfits.length) {
					html +=
						'<div class="mtm-error-message" role="alert">' +
						escapeHtml(
							'A ' +
								heading.label +
								' heading is made ' +
								misfits
									.map(function (dimension) {
										var limit = heading[dimension.key];
										return (
											'between ' +
											self.length(limit.min) +
											' and ' +
											self.length(limit.max) +
											' ' +
											dimension.adverb
										);
									})
									.join(' and ') +
								', so it cannot be made at your ' +
								misfits
									.map(function (dimension) {
										return (
											dimension.field +
											' of ' +
											self.length(self.enteredMm(dimension.field))
										);
									})
									.join(' and ') +
								'. Please choose another heading, or go back and change your ' +
								(misfits.length === 1 ? misfits[0].field : 'size') +
								'.',
						) +
						'</div>';
				} else if (blocked.length) {
					// Only the measurements actually doing the blocking are quoted back:
					// naming a drop that suits every tile would read as if it were at fault.
					var blocking = DIMENSIONS.filter(function (dimension) {
						return blocked.some(function (item) {
							return !self.headingFitsDimension(item, dimension);
						});
					});
					var entered = blocking.map(function (dimension) {
						var mm = self.enteredMm(dimension.field);
						return mm == null ? null : self.length(mm) + ' ' + dimension.adverb;
					});

					if (
						entered.length &&
						entered.every(function (part) {
							return part;
						})
					)
						html +=
							'<div class="mtm-callout">' +
							icon('mtm-i-info', 'mtm-callout__icon') +
							'<span>' +
							escapeHtml(
								(blocked.length === 1
									? blocked[0].label + ' is not available'
									: 'Some headings are not available') +
									' at ' +
									entered.join(' × ') +
									'. Go back to change your ' +
									(blocking.length === 1 ? blocking[0].field : 'measurements') +
									' if you need ' +
									(blocked.length === 1 ? 'it' : 'one of them') +
									'.',
							) +
							'</span>' +
							'</div>';
				}

				if (heading && heading.extra === 'eyelet') {
					var eyeletTiles = EYELET_COLOURS.map(function (colour) {
						return self.tile('eyeletColour', colour.key, colour.label, {
							icon: colour.icon,
							className: 'mtm-tile--ring',
							selected: self.state.eyeletColour === colour.key,
							disabled: !self.choiceByCode(
								heading.blindtypeId,
								GROUP.EYELET,
								colour.code,
							),
						});
					}).join('');

					html += this.field('Eyelet colour', eyeletTiles, {
						tilesClass: 'mtm-tiles--3',
						missing: !this.state.eyeletColour,
						hint: 'Please select a colour',
					});
				}

				// Nothing to choose: the track comes with the heading and is in the price.
				// Motorised wave swaps it for the wireless track, which the control step
				// already explains.
				if (heading && heading.manualTrackLabel && !this.motorised()) {
					html +=
						'<p class="mtm-prose">A wave track cut to your measured width is ' +
						'supplied with this heading, together with the brackets to fit it. ' +
						'Its price is included in the total.</p>' +
						'<button type="button" class="mtm-info-link" data-mtm-drawer-open="track">' +
						icon('mtm-i-info', '') +
						'About the wave track</button>';
				}

				return (
					html +
					this.renderError() +
					this.actions('size', 'heading', {
						enabled: this.headingComplete(),
					})
				);
			}

			// ---------- step: control type (blinds always, curtains when motorisable) ----------

			renderControlPanel() {
				var self = this;
				var blindtypeId = this.currentBlindtypeId();
				var heading = this.currentHeading();
				var missing = this.controlMissing();
				var isCurtain = this.flow.key === 'curtain';
				var html = '';

				if (isCurtain) {
					var typeTiles = CONTROL_TYPES.map(function (type) {
						return self.tile('controlType', type.key, type.label, {
							selected: self.state.controlType === type.key,
						});
					}).join('');
					html +=
						'<p class="mtm-prose">This heading can be supplied with a motorised track.</p>' +
						this.field('Control type', typeTiles, {
							info: 'control',
							missing: missing.indexOf('controlType') !== -1,
							hint: 'Please choose manual or motorised',
						});
				}

				if (this.motorised()) {
					// The motor itself is not a choice — it follows the headrail, and narrow
					// blinds take a different unit. Shown so the price is explicable.
					var motorCode = this.currentMotorCode();
					var motorChoice = motorCode
						? this.choiceByCode(blindtypeId, GROUP.MOTOR, motorCode)
						: null;
					if (motorChoice) {
						// A blind was quoted the motor with its headrail, so pricing it again
						// here would read as a second charge. A curtain has not: motorisation
						// is the choice being made on this step.
						var motorNote = !isCurtain
							? ' — included in the headrail price'
							: motorChoice.price > 0
								? ' — ' + formatMoney(motorChoice.price)
								: '';
						html +=
							'<div class="mtm-rows">' +
							this.row('Motor', motorChoice.description + motorNote) +
							'</div>';
					}

					html += this.field(
						'Remote',
						REMOTES.map(function (remote) {
							var choice = self.choiceByCode(
								blindtypeId,
								GROUP.MOTOR_REMOTE,
								remote.code,
							);
							return self.tile('remote', remote.key, remote.label, {
								selected: self.state.remote === remote.key,
								disabled: !choice,
								price: choice ? choice.price : null,
							});
						}).join(''),
						{
							info: 'remote',
							missing: missing.indexOf('remote') !== -1,
							hint: 'Please choose',
						},
					);

					if (!isCurtain) {
						html += this.field(
							'Charger',
							CHARGERS.map(function (charger) {
								var choice = self.choiceByCode(
									blindtypeId,
									GROUP.CHARGER,
									charger.code,
								);
								return self.tile('charger', charger.key, charger.label, {
									selected: self.state.charger === charger.key,
									disabled: !choice,
									price: choice ? choice.price : null,
								});
							}).join(''),
							{
								missing: missing.indexOf('charger') !== -1,
								hint: 'Please choose',
							},
						);

						html += this.field(
							'Smart hub',
							HUBS.map(function (hub) {
								var choice = self.choiceByCode(
									blindtypeId,
									GROUP.HUB,
									hub.code,
								);
								return self.tile('hub', hub.key, hub.label, {
									selected: self.state.hub === hub.key,
									disabled: !choice,
									price: choice ? choice.price : null,
								});
							}).join(''),
							{
								info: 'hub',
								missing: missing.indexOf('hub') !== -1,
								hint: 'Please choose',
							},
						);
					}
				} else if (!isCurtain) {
					html += this.field(
						'Chain',
						CHAINS.map(function (chain) {
							var choice = self.choiceByCode(
								blindtypeId,
								GROUP.CHAIN,
								chain.code,
							);
							return self.tile('chain', chain.key, chain.label, {
								swatch: chain.swatch,
								selected: self.state.chain === chain.key,
								disabled: !choice,
								price: choice ? choice.price : null,
							});
						}).join(''),
						{ missing: missing.indexOf('chain') !== -1, hint: 'Please choose' },
					);
				}

				// Control Side exists on every blind type and on Wave, but not on 549.
				if (!isCurtain && this.optionsFor(blindtypeId)) {
					var sideGroup = this.optionsFor(blindtypeId)[GROUP.CONTROL_SIDE];
					if (sideGroup) {
						html += this.field(
							this.motorised() ? 'Motor side' : 'Chain side',
							CONTROL_SIDES.map(function (side) {
								var available = side.codes.some(function (code) {
									return !!self.choiceByCode(
										blindtypeId,
										GROUP.CONTROL_SIDE,
										code,
									);
								});
								return self.tile('controlSide', side.key, side.label, {
									selected: self.state.controlSide === side.key,
									disabled: !available,
								});
							}).join(''),
							{
								info: this.motorised() ? 'side' : null,
								missing: missing.indexOf('controlSide') !== -1,
								hint: 'Please choose a side',
							},
						);
					}
				}

				return (
					html +
					this.renderError() +
					this.actions('heading', 'control', {
						enabled: missing.length === 0,
						note: missing.length
							? 'Please select highlighted fields above to continue'
							: null,
					})
				);
			}

			// ---------- step: fold style (roman) ----------

			renderStylePanel() {
				var self = this;
				var blindtypeId = this.currentBlindtypeId();
				var missing = this.styleMissing();
				var heading = this.currentHeading();
				var rail = (heading && heading.rail) || 'classic';

				var tiles = ROMAN_STYLES.map(function (style) {
					var choice = self.choiceByCode(blindtypeId, GROUP.STYLE, style.code);
					return self.tile('style', style.key, style.label, {
						icon: style.icons[rail],
						description: style.description,
						selected: self.state.style === style.key,
						disabled: !choice,
						// Both are £0 today; quoted anyway so a future charge shows itself
						// rather than appearing only in the total.
						price: choice ? choice.price : null,
					});
				}).join('');

				return (
					'<p class="mtm-prose">Choose how the folds sit when your blind is raised.</p>' +
					this.field('Style', tiles, {
						tilesClass: 'mtm-tiles--detail',
						missing: missing.length > 0,
						hint: 'Please choose a style',
					}) +
					this.renderError() +
					this.actions('control', 'style', {
						enabled: missing.length === 0,
						note: missing.length
							? 'Please select highlighted fields above to continue'
							: null,
					})
				);
			}

			// ---------- step: base cloth (roller) ----------

			renderClothPanel() {
				var self = this;
				var cloths = this.availableCloths();

				var clothIds = this.config.clothFabricIds || {};

				var tiles = cloths
					.map(function (cloth) {
						// A base cloth is a fabric in its own right, so the two tiles can
						// have different answers: the light-filtering cloth of a design can
						// be in supply while its blackout twin is not.
						var reason = self.unavailableReason(Number(clothIds[cloth.key]));

						return self.tile('cloth', cloth.key, cloth.label, {
							icon: cloth.icon,
							description: cloth.description,
							selected: self.state.cloth === cloth.key,
							disabled: !!reason,
							note: reason,
							// Not an add-on: every roller blind is printed on one of these,
							// and the cloth changes the fabric the whole blind is priced
							// from rather than adding to it. So each tile quotes what the
							// finished blind would cost on that cloth — as the lining tiles
							// do, and including everything else already selected.
							//
							// A cloth illumin8 cannot supply shows no figure at all: unlike
							// a heading's "+ £12.00", this one reads as the price of the
							// whole blind, and quoting one for something unbuyable invites
							// the shopper to try for it.
							total: reason ? null : self.quoteTotal({ cloth: cloth.key }),
						});
					})
					.join('');

				return (
					'<p class="mtm-prose">Choose how much light your blind should let through. ' +
					'The base cloth changes the fabric your blind is printed on, so the price ' +
					'updates with your choice.</p>' +
					'<button type="button" class="mtm-btn mtm-btn--outline mtm-btn--full" data-mtm-drawer-open="cloth">' +
					icon('mtm-i-info', 'mtm-btn__icon') +
					'Base cloth information</button>' +
					this.field('Base cloth', tiles, {
						missing: !this.state.cloth,
						hint: 'Please choose a cloth',
						tilesClass: 'mtm-tiles--detail',
					}) +
					this.renderError() +
					this.actions('control', 'cloth', {
						enabled:
							!this.state.loading &&
							!!this.state.cloth &&
							!this.fabricUnavailable(this.currentFabricId()),
						label: this.state.loading ? 'Calculating&hellip;' : 'View summary',
						note: !this.state.cloth
							? 'Please select highlighted fields above to continue'
							: this.fabricUnavailable(this.currentFabricId())
								? 'Please choose a cloth we can make at the moment'
								: null,
					})
				);
			}

			// Previous / Next pair. `next` is the step being left, so the button reads the
			// same whichever steps the current flow actually has.
			actions(back, from, options) {
				var opts = options || {};
				var busy = opts.label && opts.label.indexOf('&hellip;') !== -1;

				return (
					'<div class="mtm-actions">' +
					'<button type="button" class="mtm-btn mtm-btn--outline" data-mtm-back="' +
					back +
					'">' +
					icon('mtm-i-arrow-left', 'mtm-btn__icon') +
					'Previous</button>' +
					'<button type="button" class="mtm-btn mtm-btn--primary" data-mtm-next="' +
					from +
					'"' +
					(opts.enabled ? '' : ' disabled') +
					'>' +
					(opts.label || 'Next') +
					(busy ? '' : icon('mtm-i-arrow-right', 'mtm-btn__icon')) +
					'</button>' +
					'</div>' +
					(opts.note
						? '<p class="mtm-actions-note">' + escapeHtml(opts.note) + '</p>'
						: '')
				);
			}

			// ---------- step 4: lining ----------

			renderLiningPanel() {
				var self = this;
				var blindtypeId = this.currentBlindtypeId();
				var missing = this.liningMissing();
				var isCurtain = this.flow.key === 'curtain';
				var linings = this.flow.linings;

				// illumin8 hold one Lining Type choice per (lining x colour), but the two
				// colours of a lining are always priced the same — verified across all six
				// blind types. So the tile can price itself before a colour is picked:
				// take whichever colour the shopper has chosen, else the first that exists.
				function liningColourKey(lining) {
					var keys = Object.keys(lining.colours);
					if (self.state.liningColour) {
						keys = [self.state.liningColour].concat(keys);
					}
					for (var i = 0; i < keys.length; i++) {
						var code = lining.colours[keys[i]];
						if (code && self.choiceByCode(blindtypeId, GROUP.LINING, code)) {
							return keys[i];
						}
					}
					return null;
				}

				var liningTiles = linings
					.map(function (lining) {
						// Offered only if at least one of its colours is live for this blind type.
						var colourKey = liningColourKey(lining);
						return self.tile('lining', lining.key, lining.label, {
							icon: lining.icon,
							description: lining.description,
							selected: self.state.lining === lining.key,
							disabled: !colourKey,
							// Not an add-on: every curtain has a lining, and the choice moves
							// the whole price rather than adding to it. So each tile quotes
							// what the finished item would cost with that lining — including
							// whatever else is already selected, interlining below included.
							total: colourKey
								? self.quoteTotal({
										lining: lining.key,
										liningColour: colourKey,
									})
								: null,
						});
					})
					.join('');

				var selectedLining = findBy(linings, 'key', this.state.lining);

				// Bonded linings are Ivory only, so the colour tiles narrow with the lining.
				var colourTiles = LINING_COLOURS.map(function (colour) {
					var code = selectedLining ? selectedLining.colours[colour.key] : null;
					var available = !!(
						code && self.choiceByCode(blindtypeId, GROUP.LINING, code)
					);
					return self.tile('liningColour', colour.key, colour.label, {
						swatch: colour.swatch,
						selected: self.state.liningColour === colour.key,
						disabled: !available,
						// A bonded lining is made in Ivory only, so the colours narrow with
						// the lining above rather than for any reason of their own.
						note: available
							? null
							: selectedLining
								? 'Not made with ' + selectedLining.label
								: 'Choose a lining first',
					});
				}).join('');

				var html =
					'<p class="mtm-prose">Select the desired ' +
					(isCurtain ? 'curtain' : 'blind') +
					' lining from the list below</p>' +
					this.field('Lining', liningTiles, {
						missing: missing.indexOf('lining') !== -1,
						tilesClass: 'mtm-tiles--detail',
					}) +
					this.field('Lining colour', colourTiles, {
						missing: missing.indexOf('liningColour') !== -1,
						hint: 'Please select a colour',
					});

				// Interlining is a curtain make-up option; illumin8 do not offer it on blinds.
				if (isCurtain) {
					var interliningTiles = INTERLINING.map(function (option) {
						var choice = self.choiceByCode(
							blindtypeId,
							GROUP.ADDITIONAL_LINING,
							option.code,
						);
						return self.tile('interlining', option.key, option.label, {
							selected: self.state.interlining === option.key,
							disabled: !choice,
							price: choice ? choice.price : null,
						});
					}).join('');

					html += this.field('Interlining', interliningTiles, {
						info: 'interlining',
						missing: missing.indexOf('interlining') !== -1,
					});
				}

				return (
					html +
					this.renderError() +
					this.actions(this.prevStep('lining') || 'heading', 'lining', {
						enabled: missing.length === 0 && !this.state.loading,
						label: this.state.loading ? 'Calculating&hellip;' : 'View summary',
						note: missing.length
							? 'Please select highlighted fields above to continue'
							: null,
					})
				);
			}

			// ---------- step 5: summary ----------

			// The configuration in words. Shared by the summary panel and the cart line, so
			// what the shopper approved and what the order carries cannot drift apart.
			summaryRows() {
				var heading = this.currentHeading();
				var rows = [];

				function add(label, value) {
					rows.push({ label: label, value: value || '\u2014' });
				}

				if (this.config.printName) add('Print', this.config.printName);

				if (this.flow.key === 'curtain') {
					var panel = findBy(PANELS, 'key', this.state.panel);
					add('Curtain type', panel && panel.label);
				} else {
					var fitting = findBy(FITTINGS, 'key', this.state.fitting);
					add('Fitting type', fitting && fitting.label);
				}

				add('Width', formatLengthFull(this.state.widthMm, this.state.unit));
				add('Drop', formatLengthFull(this.state.dropMm, this.state.unit));
				add(
					this.flow.key === 'curtain' ? 'Heading' : 'Headrail',
					heading && heading.label,
				);

				if (heading && heading.extra === 'eyelet') {
					var eyelet = findBy(EYELET_COLOURS, 'key', this.state.eyeletColour);
					add('Eyelet colour', eyelet && eyelet.label);
				}

				if (heading && heading.manualTrackLabel && !this.motorised())
					add('Track', heading.manualTrackLabel);

				if (this.flow.key === 'curtain' && heading && heading.motorisable) {
					var controlType = findBy(
						CONTROL_TYPES,
						'key',
						this.state.controlType,
					);
					add('Control type', controlType && controlType.label);
				}

				if (this.motorised()) {
					var motorCode = this.currentMotorCode();
					var motor = motorCode
						? this.choiceByCode(
								this.currentBlindtypeId(),
								GROUP.MOTOR,
								motorCode,
							)
						: null;
					if (motor) add('Motor', motor.description);

					var remote = findBy(REMOTES, 'key', this.state.remote);
					add('Remote', remote && remote.label);

					if (this.flow.key !== 'curtain') {
						var charger = findBy(CHARGERS, 'key', this.state.charger);
						add('Charger', charger && charger.label);
						var hub = findBy(HUBS, 'key', this.state.hub);
						add('Smart hub', hub && hub.label);
					}
				} else if (this.flow.key !== 'curtain') {
					var chain = findBy(CHAINS, 'key', this.state.chain);
					add('Chain', chain && chain.label);
				}

				if (this.flow.key !== 'curtain' && this.state.controlSide) {
					var side = findBy(CONTROL_SIDES, 'key', this.state.controlSide);
					add(
						this.motorised() ? 'Motor side' : 'Chain side',
						side && side.label,
					);
				}

				if (this.flow.key === 'roman') {
					var style = findBy(ROMAN_STYLES, 'key', this.state.style);
					add('Style', style && style.label);
				}

				if (this.flow.key === 'roller') {
					var cloth = findBy(BASE_CLOTHS, 'key', this.state.cloth);
					add('Base cloth', cloth && cloth.label);
				} else {
					var lining = findBy(this.flow.linings, 'key', this.state.lining);
					var liningColour = findBy(
						LINING_COLOURS,
						'key',
						this.state.liningColour,
					);
					add('Lining', lining && lining.label);
					add('Lining colour', liningColour && liningColour.label);
				}

				if (this.flow.key === 'curtain') {
					var interlining = findBy(INTERLINING, 'key', this.state.interlining);
					add('Interlining', interlining && interlining.label);
				}

				return rows;
			}

			renderSummaryPanel() {
				var self = this;
				var total = this.state.total;

				var rows = this.summaryRows()
					.map(function (entry) {
						return self.row(entry.label, entry.value);
					})
					.join('');

				rows += this.priceRow('Total Price', total);

				var back = this.prevStep('summary') || 'heading';

				return (
					'<div class="mtm-rows">' +
					rows +
					'</div>' +
					this.renderError() +
					'<div class="mtm-actions">' +
					'<button type="button" class="mtm-btn mtm-btn--outline" data-mtm-back="' +
					back +
					'">' +
					icon('mtm-i-arrow-left', 'mtm-btn__icon') +
					'Previous</button>' +
					'<button type="button" class="mtm-btn mtm-btn--primary" data-mtm-add' +
					(total == null ||
					this.state.adding ||
					this.fabricUnavailable(this.currentFabricId())
						? ' disabled'
						: '') +
					'>' +
					(this.state.adding ? 'Adding&hellip;' : 'Add to bag') +
					'</button>' +
					'</div>'
				);
			}

			// One selectable tile. Every step builds its options from this, so a tile looks
			// and behaves the same whether it carries artwork, a colour chip or just a label.
			tile(field, value, label, options) {
				var opts = options || {};

				// Two kinds of price. Most tiles are add-ons to a configuration that would
				// be complete without them, so they read "+ £12.00". A lining is not: every
				// curtain has one, the choice is between alternatives, and "+ £12.00" says
				// nothing about what the curtain costs with it. Those carry `total` — the
				// price of the whole thing, so the alternatives can be compared as prices
				// rather than as differences.
				var price = null;
				if (opts.total != null) {
					price = 'Total ' + formatMoney(opts.total);
				} else if (opts.price != null && opts.price > 0) {
					price = '+ ' + formatMoney(opts.price);
				}

				return (
					'<button type="button" class="mtm-tile' +
					(opts.className ? ' ' + opts.className : '') +
					'" data-mtm-set="' +
					field +
					'" data-mtm-value="' +
					escapeHtml(value) +
					'"' +
					' aria-pressed="' +
					(opts.selected ? 'true' : 'false') +
					'"' +
					(opts.disabled ? ' disabled' : '') +
					'>' +
					// Artwork and title travel together in one element: it changes nothing
					// in the stacked tile, but lets the mobile lining row keep the title
					// under its picture while the description sits alongside both.
					'<span class="mtm-tile__media">' +
					(opts.icon ? icon(opts.icon, 'mtm-tile__icon') : '') +
					(opts.swatch
						? '<span class="mtm-tile__chip" style="background-color: ' +
							opts.swatch +
							';"></span>'
						: '') +
					'<span class="mtm-tile__label">' +
					escapeHtml(label) +
					'</span>' +
					'</span>' +
					(opts.meta && opts.meta.length
						? '<span class="mtm-tile__meta">' +
							opts.meta
								.map(function (line) {
									return escapeHtml(line);
								})
								.join('<br>') +
							'</span>'
						: '') +
					(opts.description
						? '<span class="mtm-tile__desc">' +
							escapeHtml(opts.description) +
							'</span>'
						: '') +
					// A disabled tile says nothing about itself: greying out reads as
					// "unavailable" without saying why, and the shopper cannot tell whether
					// an earlier answer of theirs is the cause. Where we know the reason,
					// the tile states it.
					(opts.note
						? '<span class="mtm-tile__note">' + escapeHtml(opts.note) + '</span>'
						: '') +
					(price
						? '<span class="mtm-tile__price">' + escapeHtml(price) + '</span>'
						: '') +
					'</button>'
				);
			}

			// A labelled group of tiles. An unanswered field is not coloured — the prompt
			// under the Next button carries that job — but it still shows its own hint.
			// The label is wrapped in a flex row only when it has an ⓘ to sit beside.
			field(label, tiles, options) {
				var opts = options || {};

				var labelHtml =
					'<span class="mtm-field-label">' +
					escapeHtml(label) +
					':' +
					(opts.missing && opts.hint
						? ' <span class="mtm-field-label__hint">' +
							escapeHtml(opts.hint) +
							'</span>'
						: '') +
					'</span>';

				if (opts.info) {
					labelHtml =
						'<span class="mtm-label-row">' +
						labelHtml +
						'<button type="button" class="mtm-info-button" data-mtm-drawer-open="' +
						opts.info +
						'" aria-label="' +
						escapeHtml(label) +
						' information">' +
						icon('mtm-i-info', '') +
						'</button>' +
						'</span>';
				}

				return (
					'<div>' +
					labelHtml +
					'<div class="mtm-tiles' +
					(opts.tilesClass ? ' ' + opts.tilesClass : '') +
					'">' +
					tiles +
					'</div>' +
					'</div>'
				);
			}

			// A highlighted price row. Under the made-to-measure discount it reads
			// "Was: £100.00 / Now: £85.00" instead of the bare price.
			priceRow(label, amount) {
				var percent = Number(this.config.discountPercent) || 0;
				if (amount == null || percent <= 0) {
					return this.row(
						label,
						amount == null ? '\u2014' : formatMoney(amount),
						true,
					);
				}

				return (
					'<div class="mtm-row mtm-row--total">' +
					'<span class="mtm-row__label">' +
					escapeHtml(label) +
					'</span>' +
					'<span class="mtm-row__value mtm-was-now">' +
					'<span class="mtm-was-now__was">Was: <s>' +
					escapeHtml(formatMoney(amount)) +
					'</s></span>' +
					'<span class="mtm-was-now__now">Now: ' +
					escapeHtml(formatMoney(discountedPrice(amount, percent))) +
					'</span>' +
					'</span>' +
					'</div>'
				);
			}

			row(label, value, isTotal) {
				return (
					'<div class="mtm-row' +
					(isTotal ? ' mtm-row--total' : '') +
					'">' +
					'<span class="mtm-row__label">' +
					escapeHtml(label) +
					'</span>' +
					'<span class="mtm-row__value">' +
					escapeHtml(value) +
					'</span>' +
					'</div>'
				);
			}

			renderError() {
				if (!this.state.error) return '';
				return (
					'<div class="mtm-error-message" role="alert">' +
					escapeHtml(this.state.error) +
					'</div>'
				);
			}

			// ============================================================
			// Events
			// ============================================================

			onClick(event) {
				// The sample button is an anchor so that it still works without JS; with JS
				// its href is never followed, the sample goes straight into the bag instead.
				var sample = event.target.closest('[data-mtm-sample]');
				if (
					sample &&
					this.contains(sample) &&
					Number(this.config.sampleVariantId || 0)
				) {
					event.preventDefault();
					this.addSample(sample);
					return;
				}

				var target = event.target.closest('button');
				if (!target || !this.contains(target)) return;

				if (target.hasAttribute('data-mtm-start')) {
					event.preventDefault();
					this.setState({ started: true, step: 'size' });
					return;
				}

				if (target.hasAttribute('data-mtm-set')) {
					event.preventDefault();
					this.onSelect(
						target.getAttribute('data-mtm-set'),
						target.getAttribute('data-mtm-value'),
					);
					return;
				}

				if (target.hasAttribute('data-mtm-step-header')) {
					event.preventDefault();
					var step = target.getAttribute('data-mtm-step-header');
					if (
						step !== this.state.step &&
						this.stepIndex(step) <= this.state.furthest
					) {
						this.state.error = '';
						this.goToStep(step);
					}
					return;
				}

				if (target.hasAttribute('data-mtm-back')) {
					event.preventDefault();
					this.state.error = '';
					this.goToStep(target.getAttribute('data-mtm-back'));
					return;
				}

				if (target.hasAttribute('data-mtm-next')) {
					event.preventDefault();
					this.onNext(target.getAttribute('data-mtm-next'));
					return;
				}

				if (target.hasAttribute('data-mtm-add')) {
					event.preventDefault();
					this.addToBag();
					return;
				}

				if (target.hasAttribute('data-mtm-drawer-open')) {
					event.preventDefault();
					this.openDrawer(target.getAttribute('data-mtm-drawer-open'));
				}
			}

			onSelect(field, value) {
				var patch = {};
				patch[field] = value;
				patch.error = '';

				// Any selection invalidates the priced total; it is recomputed on the way
				// into the summary, against whatever blind type the heading resolves to.
				patch.total = null;

				if (field === 'heading') {
					// Eyelet colour and the motorised choices belong to one heading each —
					// carrying them across would send a choice the new blind type does
					// not have.
					var next = findBy(this.flow.headings, 'key', value);
					if (!next || next.extra !== 'eyelet') patch.eyeletColour = null;
					if (!next || !next.motorisable) patch.controlType = null;

					// Manual and motorised are separate blind types with separate menus.
					patch.chain = null;
					patch.remote = null;
					patch.charger = null;
					patch.hub = null;
					patch.controlSide = null;

					// Roman bonded linings exist on both 581 and 582, but a lining chosen
					// against one blind type still has to be re-resolved against the other.
					if (this.flow.key === 'roman') patch.liningColour = null;
				}

				if (field === 'controlType') {
					patch.remote = null;
					patch.charger = null;
					patch.hub = null;
				}

				// Choosing Exact rules out the no-drill headrail; drop it rather than
				// leaving a selection the shopper can no longer see.
				if (field === 'fitting') {
					var heading = this.currentHeading();
					if (
						heading &&
						heading.requiresFitting &&
						heading.requiresFitting !== value
					) {
						patch.heading = null;
					}
					// Fitting Type is the measureid, which is part of the fabric price key.
					patch.basePrice = null;
				}

				// The roller base cloth is a different fabric, so every cached price for the
				// previous cloth is stale — and so is the option menu, which is keyed on
				// fabricid because illumin8 price options per fabric margin band. Reload it
				// so stepping back to an earlier step still finds its choices.
				if (field === 'cloth') {
					patch.basePrice = null;
					this.setState(patch);
					this.ensureOptions();
					return;
				}

				if (field === 'lining') {
					var lining = findBy(this.flow.linings, 'key', value);
					var current = this.state.liningColour;
					// Drop a colour the new lining does not offer (bonded linings are Ivory only).
					if (
						current &&
						lining &&
						!this.choiceByCode(
							this.currentBlindtypeId(),
							GROUP.LINING,
							lining.colours[current],
						)
					) {
						patch.liningColour = null;
					}
				}

				this.setState(patch);
			}

			// Radios and <select> both raise `input`, so the unit radios and the inch
			// fraction menus arrive here alongside the number fields.
			onInput(event) {
				var target = event.target;

				if (target.hasAttribute && target.hasAttribute('data-mtm-unit')) {
					this.onUnitChange(target.value);
					return;
				}

				var control =
					target.closest('[data-mtm-input]') ||
					target.closest('[data-mtm-fraction]');
				if (!control) return;

				var field =
					control.getAttribute('data-mtm-input') ||
					control.getAttribute('data-mtm-fraction');

				this.setMeasurement(field, this.readMeasurement(field));
			}

			// Read from the controls rather than from the event, so that the two halves of
			// an inch entry always combine into one millimetre figure however either half
			// was changed.
			readMeasurement(field) {
				var input = this.querySelector('[data-mtm-input="' + field + '"]');
				if (!input) return null;

				var raw = input.value.trim();
				if (raw === '') return null;

				if (this.state.unit === 'in') {
					var whole = parseInt(raw, 10);
					if (isNaN(whole)) return null;
					var select = this.querySelector(
						'[data-mtm-fraction="' + field + '"]',
					);
					var eighths = select ? parseInt(select.value, 10) : 0;
					return mmFromInches(whole, isNaN(eighths) ? 0 : eighths);
				}

				var value = parseFloat(raw);
				if (isNaN(value)) return null;
				return this.state.unit === 'cm' ? mmFromCm(value) : Math.round(value);
			}

			// Written straight to state (no re-render) so typing is not interrupted; the
			// Next button's disabled state is the only thing that needs to keep up.
			setMeasurement(field, mm) {
				this.state[field === 'width' ? 'widthMm' : 'dropMm'] = mm;
				this.state.total = null;
				this.state.basePrice = null;

				this.unpickUnbuildableHeading();

				var next = this.querySelector('[data-mtm-next="size"]');
				if (next) next.disabled = !this.sizeComplete() || this.state.loading;
			}

			// A size the chosen heading cannot be made to unpicks every step after it, so
			// the shopper cannot jump back to the summary on a stale step header without
			// answering the heading step again.
			unpickUnbuildableHeading() {
				if (this.headingFitsSize(this.currentHeading())) return;
				var headingIndex = this.stepIndex('heading');
				if (headingIndex > -1 && this.state.furthest > headingIndex)
					this.state.furthest = headingIndex;
			}

			// Switching unit re-expresses what has already been entered rather than
			// clearing it. Inches can only say every 3.175mm, so anything carried into them
			// is snapped onto the nearest eighth AS IT CROSSES: the shopper then sees the
			// exact size that will be priced and built, not a rounded display sitting over
			// a different figure. Any price already quoted is dropped with it.
			onUnitChange(unit) {
				if (!findBy(UNITS, 'key', unit) || unit === this.state.unit) return;

				storeUnit(unit);
				this.state.unit = unit;

				if (unit === 'in') {
					this.state.widthMm = this.snapToEnterable(
						this.state.widthMm,
						'width',
					);
					this.state.dropMm = this.snapToEnterable(this.state.dropMm, 'drop');
				}

				// An error already on screen was worded in the old unit; restate it in the
				// new one rather than quoting centimetres at someone now working in inches.
				// Only fields already showing an error are touched — switching unit must not
				// be what first tells someone their drop is too long.
				var self = this;
				var errors = this.sizeErrors();
				var refreshed = {};
				['width', 'drop'].forEach(function (field) {
					if (self.state.fieldErrors[field] && errors[field])
						refreshed[field] = errors[field];
				});
				this.state.fieldErrors = refreshed;

				this.state.total = null;
				this.state.basePrice = null;
				this.unpickUnbuildableHeading();
				this.render();

				// render() replaces the panel wholesale, so the radio that was just operated
				// is gone. Focus its replacement, or a keyboard user loses the radiogroup
				// after a single arrow press.
				var radio = this.querySelector('[data-mtm-unit][value="' + unit + '"]');
				if (radio) radio.focus();
			}

			onKeydown(event) {
				if (event.key !== 'Enter') return;
				var input = event.target.closest('[data-mtm-input]');
				if (!input) return;
				event.preventDefault();
				this.onNext('size');
			}

			// ---------- step transitions ----------

			onNext(step) {
				var self = this;
				var after = this.nextStep(step);

				if (step === 'size') {
					var errors = this.sizeErrors();
					if (Object.keys(errors).length) {
						this.setState({ fieldErrors: errors });
						return;
					}
					if (!this.sizeComplete()) return;

					// Dimensions changed → every cached price for the old size is stale.
					this.setState({ fieldErrors: {}, loading: true, error: '' });

					this.loadFabricPrice(
						this.flow.defaultBlindtypeId,
						this.currentMeasureId(),
					)
						.then(function (price) {
							self.state.basePrice = price;
							self.state.loading = false;
							self.goToStep('confirm');
						})
						.catch(function (error) {
							self.setState({ loading: false, error: error.message });
						});
					return;
				}

				if (step === 'confirm') {
					// Price every heading tile: they span several blind types, each with its
					// own menu. Fetched in parallel — illumin8 could not serve these
					// concurrently until 2026-08-17, and the guards below are what let us
					// trust that they can now.
					var blindtypeIds = this.headingBlindtypeIds();

					this.setState({ loading: true, error: '' });

					Promise.all(
						blindtypeIds.map(function (blindtypeId) {
							return self.loadOptions(blindtypeId).catch(function () {
								return null;
							});
						}),
					).then(function () {
						self.state.loading = false;

						// Every menu, or none of them. A partly loaded set is what the shopper
						// sees as "this fabric only comes with a Wave heading" — a truncated
						// choice, indistinguishable from a real one, that quietly costs a sale
						// or sells the wrong thing. Failures are not cached, so pressing
						// Continue again retries.
						var missing = blindtypeIds.filter(function (blindtypeId) {
							return !self.optionsFor(blindtypeId);
						});
						if (missing.length) {
							self.setState({
								error:
									'Options are unavailable right now. Please try again shortly.',
							});
							return;
						}
						self.goToStep('heading');
					});
					return;
				}

				if (step === 'heading') {
					if (!this.headingComplete()) return;
					if (after === 'summary') return this.priceAndSummarise();
					this.goToStep(after);
					return;
				}

				if (step === 'control') {
					if (this.controlMissing().length) return;
					if (after === 'summary') return this.priceAndSummarise();
					this.goToStep(after);
					return;
				}

				if (step === 'style') {
					if (this.styleMissing().length) return;
					if (after === 'summary') return this.priceAndSummarise();
					this.goToStep(after);
					return;
				}

				if (step === 'lining' || step === 'cloth') {
					if (this.lastStepMissing().length) return;
					this.priceAndSummarise();
				}
			}

			// Fetch the fabric price for the fully configured blind type, total it up, and
			// open the summary. computeTotal() throws when a group has no reviewed default.
			//
			// The option menu is (re)loaded alongside it because the roller base cloth
			// changes the fabricid, and both caches are keyed on that — without this, the
			// menu cached against the provisional cloth would be missed and every choice
			// would resolve to nothing.
			priceAndSummarise() {
				var self = this;
				this.setState({ loading: true, error: '' });

				Promise.all([
					this.loadOptions(this.currentBlindtypeId()),
					this.loadFabricPrice(
						this.currentBlindtypeId(),
						this.currentMeasureId(),
					),
				])
					.then(function () {
						self.state.total = self.computeTotal();
						self.state.loading = false;
						if (self.state.total == null) {
							self.setState({
								error:
									'Unable to calculate a price for this configuration. Please try again.',
							});
							return;
						}
						self.goToStep('summary');
					})
					.catch(function (error) {
						self.setState({ loading: false, error: error.message });
					});
			}

			// ---------- add to bag ----------

			addToBag() {
				var self = this;
				var variantId = Number(this.config.variantId || 0);

				if (!variantId) {
					this.setState({
						error:
							'This product is not set up for made to measure yet. Please contact us.',
					});
					return;
				}

				// Last line of defence on the heading's size limits: the summary is normally
				// out of reach once a measurement stops fitting, so this only fires if a
				// stale view is clicked. The heading step is where the reason is explained.
				if (!this.headingFitsSize(this.currentHeading())) {
					this.state.error = '';
					this.goToStep('heading');
					return;
				}

				var chosen;
				try {
					chosen = this.selectedChoices();
				} catch (error) {
					this.setState({ error: error.message });
					return;
				}

				if (!chosen) {
					this.setState({
						error: 'Unable to confirm your configuration. Please try again.',
					});
					return;
				}

				var choiceIds = chosen.map(function (entry) {
					return entry.choice.choiceId;
				});

				this.setState({ adding: true, error: '' });

				this.fetchSignedQuote(choiceIds)
					.then(function (quote) {
						return self.addLine(variantId, quote, chosen);
					})
					.then(function () {
						self.state.adding = false;
						self.render();
					})
					.catch(function (error) {
						// The 422 is fresher than the map this page fetched, which is the
						// whole point of it — so fold it in. The base-cloth tile greys out,
						// the notice appears and Add to bag stays disabled, instead of the
						// shopper clicking into the same refusal again. Their selection is
						// left exactly as they made it; nothing is swapped underneath them.
						if (error.mtmReason === 'fabric_unavailable') {
							self.markUnavailable(error.mtmFabricId, error.mtmEta);
						}

						self.setState({
							adding: false,
							error: error.message || 'Add to bag failed. Please try again.',
						});
					});
			}

			// Record what /mtm/price just told us, for a page whose availability call
			// happened before illumin8 deactivated the cloth.
			markUnavailable(fabricId, eta) {
				var id = Number(fabricId) || this.currentFabricId();
				if (!id) return;

				if (!this.availability) this.availability = {};
				this.availability[String(id)] = { available: false, eta: eta || null };

				debugLine('availability: ' + id + ' refused at quote time');
			}

			fetchSignedQuote(choiceIds) {
				var body = {
					blindtypeid: this.currentBlindtypeId(),
					// currentFabricId(), not config.fabricId: a roller is priced against its
					// chosen base cloth, which is a fabric of its own. This line predates the
					// roller flow — with the product-level metafield it either signed nothing
					// (a roller product carries no plain illumin8_fabric_id) or signed the
					// curtain cloth's id, which is a different blind from the one quoted.
					fabricid: this.currentFabricId(),
					measureid: this.currentMeasureId(),
					width_mm: this.state.widthMm,
					drop_mm: this.state.dropMm,
					choice_ids: choiceIds,
				};

				var url = this.base + '/mtm/price';
				var requestId = debugRequest('POST', url, body);
				var startedAt = debugNow();

				return fetch(url, {
					method: 'POST',
					headers: {
						'Content-Type': 'application/json',
						Accept: 'application/json',
					},
					body: JSON.stringify(body),
				})
					.then(function (response) {
						if (!response.ok) {
							// The body is read whatever the status, because a 422 is how the
							// middleware says WHICH fabric it will not price and why — copy
							// the shopper needs, not just "try again". Everything else falls
							// back to the generic sentence, as before.
							return response.text().then(function (text) {
								debugResponse(requestId, response.status, startedAt, text);

								var payload = null;
								try {
									payload = JSON.parse(text);
								} catch (parseError) {
									payload = null;
								}

								var quoteError = new Error(
									quoteRefusalMessage(payload) ||
										'Unable to confirm the price. Please try again.',
								);
								quoteError.mtmReason = payload && payload.reason;
								quoteError.mtmFabricId = payload && payload.fabricid;
								quoteError.mtmEta = (payload && payload.eta) || null;
								quoteError.mtmLogged = true;
								throw quoteError;
							});
						}
						return response.json().then(function (payload) {
							debugResponse(
								requestId,
								response.status,
								startedAt,
								debugQuote(payload),
							);
							return payload;
						});
					})
					.catch(function (error) {
						debugFailure(requestId, startedAt, error);
						throw error;
					})
					.then(function (quote) {
						// Every field the signature covers has to come back, because the cart
						// line is written from THIS response. A quote missing one would add a
						// line the functions cannot verify, which blocks checkout at the far
						// end — better to fail here, where the shopper can retry.
						if (
							!quote ||
							!quote.sig ||
							String(quote.sig_v) !== MTM_SIG_VERSION ||
							quote.price_pence == null ||
							quote.expires_at == null
						) {
							throw new Error(
								'The pricing service returned an invalid quote. Please try again.',
							);
						}
						return quote;
					});
			}

			addLine(variantId, quote, chosen) {
				var self = this;

				// Exactly what the shopper approved on the summary step — same source, so
				// the cart line cannot describe a different blind from the one priced.
				var properties = {};
				this.summaryRows().forEach(function (entry) {
					properties[entry.label] = entry.value;
				});

				// Hidden config, taken from the SERVER-echoed canonical values so the cart
				// transform / validation functions rebuild the same HMAC input byte for byte
				// (the sorted `_choice_ids` especially). Also the EDI purchase order's inputs.
				// Which signature layout the functions should rebuild. A line without it is
				// rejected rather than verified under a guessed one.
				properties._sig_v = String(quote.sig_v);
				properties._blindtypeid = String(quote.blindtypeid);
				properties._fabricid = String(quote.fabricid);
				properties._measureid = String(quote.measureid);
				properties._width_mm = String(quote.width_mm);
				properties._drop_mm = String(quote.drop_mm);
				properties._choice_ids = String(quote.choice_ids);
				// The unit the shopper measured in. The size itself is millimetres wherever
				// it is read — this only says how the visible Width/Drop rows were written,
				// so a size query can be answered in the unit it was ordered in.
				properties._display_unit = String(this.state.unit);
				properties._price_pence = String(quote.price_pence);
				properties._expires_at = String(quote.expires_at);
				properties._price_sig = String(quote.sig);

				var root = cartRoot();
				var sections = bundledSections();

				var payload = {
					id: variantId,
					quantity: this.readQuantity(),
					properties: properties,
				};

				// Asked for only where something is listening for them, so the request
				// 5.7 sends is byte for byte the one it sent before.
				if (sections.length) {
					payload.sections = sections.join(',');
					payload.sections_url = root + 'variants/' + variantId;
				}

				var requestId = debugRequest('POST', root + 'cart/add.js', payload);
				var startedAt = debugNow();

				return fetch(root + 'cart/add.js', {
					method: 'POST',
					headers: {
						'Content-Type': 'application/json',
						'X-Requested-With': 'XMLHttpRequest',
					},
					body: JSON.stringify(payload),
				})
					.then(function (response) {
						return response.json().then(function (body) {
							debugResponse(requestId, response.status, startedAt, body);
							if (!response.ok)
								throw new Error(body.description || 'Add to bag failed.');
							return body;
						});
					})
					.then(function (item) {
						return self.notifyCart(item, root, sections);
					});
			}

			// The theme's own quantity selector sits above the creator (see the designs).
			readQuantity() {
				var scope =
					this.closest('.product-info, .Product__Info, .shopify-section') ||
					document;
				var input = scope.querySelector(
					'input[name="quantity"], .QuantitySelector__CurrentQuantity',
				);
				var quantity = input ? parseInt(input.value, 10) : 1;
				return quantity > 0 ? quantity : 1;
			}

			// Both Prestige generations are supported: 10.6 listens for `cart:change` /
			// `cart:refresh` on the document, 5.7 for `product:added` on the product section.
			// `sections` is what `bundledSections()` collected before the add — empty on
			// 5.7, one or more section ids on 10.6.
			notifyCart(item, root, sections) {
				var self = this;
				var bundled = sections || [];
				var rendered = (item && item.sections) || null;

				if (window.themeVariables && window.themeVariables.settings) {
					var settings = window.themeVariables.settings;
					if (settings.cartType === 'page' || settings.pageType === 'cart') {
						window.location.href = root + 'cart';
						return Promise.resolve();
					}
				}

				// The drawer is opened either way, so a cart that would not load is not
				// worth failing over here — the line is already added.
				return this.fetchCart(root)
					.catch(function () {
						return null;
					})
					.then(function (cart) {
						// 10.6 re-renders the drawer from the section HTML hanging off the
						// cart it is handed, so the markup the add returned is carried over
						// verbatim — /cart.js on its own never has it.
						if (cart && bundled.length && rendered) cart.sections = rendered;

						// Every 10.6 listener dereferences `detail.cart` (the drawer goes on
						// into `cart.sections[id]`), so the event is only fired when both are
						// in hand. Anything short of that falls through to the refresh below,
						// which reads the cart from the server instead.
						var announced = !!cart && (!bundled.length || !!rendered);

						if (announced) {
							document.documentElement.dispatchEvent(
								new CustomEvent('cart:change', {
									bubbles: true,
									detail: {
										baseEvent: 'variant:add',
										onSuccessDo: 'force_open_drawer',
										cart: cart,
									},
								}),
							);
						}

						// 5.7's mini-cart re-renders on this, and it is how 10.6 recovers
						// when the sections did not come back. Skipped only on the 10.6 path
						// where `cart:change` has already replaced the drawer's contents, so
						// the drawer is not re-rendered underneath its own opening animation.
						if (!(bundled.length && announced)) {
							document.dispatchEvent(new CustomEvent('cart:refresh'));
						}

						// Prestige 5.7 (develop store).
						self.dispatchEvent(
							new CustomEvent('product:added', {
								bubbles: true,
								detail: {
									variant: { id: item.variant_id || item.id },
									// Explicitly 0 when nothing was added — the theme adds this
									// to its own item count before re-reading the cart.
									quantity: item.quantity == null ? 1 : item.quantity,
								},
							}),
						);

						// 10.6 opens the drawer only off `cart:change`. Where that could not
						// be fired the drawer is opened by hand, so the bag still shows.
						if (bundled.length && !announced) self.openCartDrawer();
					});
			}

			// The 10.6 drawer element, opened directly. 5.7 has no such element, so this
			// finds nothing and does nothing there.
			openCartDrawer() {
				var drawer = document.querySelector('cart-drawer');
				if (!drawer || typeof drawer.show !== 'function') return;

				try {
					drawer.show();
				} catch (error) {
					debugGroup('cart drawer would not open', function () {
						console.log(error);
					});
				}
			}

			// The cart as Shopify has it. Rejects on failure, so a caller that cannot act
			// without knowing what is in the bag does not act on a guess.
			fetchCart(root) {
				var requestId = debugRequest('GET', root + 'cart.js');
				var startedAt = debugNow();

				return fetch(root + 'cart.js', {
					headers: { Accept: 'application/json' },
				})
					.then(function (response) {
						return response.json().then(function (cart) {
							debugResponse(requestId, response.status, startedAt, cart);
							if (!response.ok || !cart || !Array.isArray(cart.items)) {
								throw new Error('Could not read your bag. Please try again.');
							}
							return cart;
						});
					})
					.catch(function (error) {
						debugFailure(requestId, startedAt, error);
						throw error;
					});
			}

			// ---------- fabric sample ----------

			// The sample is an ordinary Shopify product sold per fabric, so it needs no
			// quote and no signature — a plain cart/add.js, then the theme's cart opens on
			// the same events the made-to-measure add uses.
			addSample(trigger) {
				var self = this;
				if (this.sampleAdding) return;

				var variantId = Number(this.config.sampleVariantId || 0);
				var root = cartRoot();

				this.sampleAdding = true;
				this.setSampleBusy(trigger, true);
				this.showSampleError('');

				this.fetchCart(root)
					.then(function (cart) {
						// One swatch of a fabric is enough. A second click opens the cart to
						// show the shopper the one already in it instead of adding another.
						if (findCartLine(cart, variantId)) {
							return self.notifyCart(
								{ variant_id: variantId, quantity: 0 },
								root,
							);
						}

						// Counted across the whole bag, not just this fabric — the cap is on
						// samples as a whole, whichever product page they were added from.
						if (
							countCartSamples(cart, self.config.sampleProductType) >=
							SAMPLE_LIMIT
						) {
							throw new Error(
								'You can order up to ' +
									SAMPLE_LIMIT +
									' samples at a time. Remove one from your bag to swap it for this fabric.',
							);
						}

						return self.addSampleLine(variantId, root);
					})
					.then(function () {
						self.sampleAdding = false;
						self.setSampleBusy(trigger, false);
					})
					.catch(function (error) {
						self.sampleAdding = false;
						self.setSampleBusy(trigger, false);
						self.showSampleError(
							error.message || 'Could not add the sample. Please try again.',
						);
					});
			}

			addSampleLine(variantId, root) {
				var self = this;

				// Always one: the quantity selector above the creator belongs to the
				// made-to-measure product, not to the swatch.
				var payload = { id: variantId, quantity: 1 };
				var sections = bundledSections();

				if (sections.length) {
					payload.sections = sections.join(',');
					payload.sections_url = root + 'variants/' + variantId;
				}

				var requestId = debugRequest('POST', root + 'cart/add.js', payload);
				var startedAt = debugNow();

				return fetch(root + 'cart/add.js', {
					method: 'POST',
					headers: {
						'Content-Type': 'application/json',
						'X-Requested-With': 'XMLHttpRequest',
					},
					body: JSON.stringify(payload),
				})
					.then(function (response) {
						return response.json().then(function (body) {
							debugResponse(requestId, response.status, startedAt, body);
							if (!response.ok) {
								throw new Error(
									body.description || 'Could not add the sample.',
								);
							}
							return body;
						});
					})
					.then(function (item) {
						return self.notifyCart(item, root, sections);
					});
			}

			// The button is outside the step accordion, so it is updated in place rather
			// than re-rendered.
			setSampleBusy(trigger, busy) {
				if (!trigger) return;

				if (busy) {
					if (this.sampleLabel == null) this.sampleLabel = trigger.innerHTML;
					trigger.setAttribute('aria-busy', 'true');
					trigger.classList.add('mtm-is-loading');
					trigger.textContent = 'Adding…';
					return;
				}

				trigger.removeAttribute('aria-busy');
				trigger.classList.remove('mtm-is-loading');
				if (this.sampleLabel != null) trigger.innerHTML = this.sampleLabel;
			}

			showSampleError(message) {
				if (!this.sampleErrorEl) return;
				this.sampleErrorEl.textContent = message || '';
				this.sampleErrorEl.hidden = !message;
			}

			// ---------- information drawer ----------

			openDrawer(key) {
				if (!this.drawerEl) return;

				var source = this.querySelector(
					'[data-mtm-drawer-content="' + key + '"]',
				);
				var target = this.drawerEl.querySelector('[data-mtm-drawer-body]');
				if (!source || !target) return;

				target.innerHTML = source.innerHTML;

				// The creator sits inside the product-info column, which both themes make a
				// sticky positioning context. A position:fixed child is trapped in that
				// context, so NO z-index can lift the drawer above the site header — it has
				// to be re-parented to <body> for the duration. (This is what the live
				// theme's own x-drawer does.) It goes home again on close so the theme
				// editor and any section re-render still find it where the Liquid put it.
				if (this.drawerEl.parentElement !== document.body) {
					this._drawerHome = this.drawerEl.parentElement;
					document.body.appendChild(this.drawerEl);
				}

				this.drawerEl.hidden = false;
				// Next frame, so the transform transition has a start value to animate from.
				requestAnimationFrame(
					function () {
						this.drawerEl.classList.add('is-open');
					}.bind(this),
				);
				document.documentElement.classList.add('mtm-scroll-locked');

				// Send focus back where it came from once the drawer closes.
				this._drawerOpener =
					document.activeElement && document.activeElement !== document.body
						? document.activeElement
						: null;

				this._onDrawerKeydown = function (event) {
					if (event.key === 'Escape') {
						this.closeDrawer();
					} else if (event.key === 'Tab') {
						this.trapFocus(event);
					}
				}.bind(this);
				document.addEventListener('keydown', this._onDrawerKeydown);

				var close = this.drawerEl.querySelector('[data-mtm-drawer-close]');
				if (close) close.focus();
			}

			// Keep Tab inside the open drawer — it covers the page, so tabbing to the
			// header behind it would be a trap of the worse kind.
			trapFocus(event) {
				var focusable = this.drawerEl.querySelectorAll(
					'a[href], button:not([disabled]), input, select, textarea, [tabindex]:not([tabindex="-1"])',
				);
				if (!focusable.length) return;

				var first = focusable[0];
				var last = focusable[focusable.length - 1];

				if (event.shiftKey && document.activeElement === first) {
					event.preventDefault();
					last.focus();
				} else if (!event.shiftKey && document.activeElement === last) {
					event.preventDefault();
					first.focus();
				}
			}

			closeDrawer() {
				if (!this.drawerEl) return;
				this.drawerEl.classList.remove('is-open');
				document.documentElement.classList.remove('mtm-scroll-locked');

				if (this._onDrawerKeydown) {
					document.removeEventListener('keydown', this._onDrawerKeydown);
					this._onDrawerKeydown = null;
				}

				if (this._drawerOpener) {
					this._drawerOpener.focus();
					this._drawerOpener = null;
				}

				var self = this;
				setTimeout(function () {
					if (self.drawerEl.classList.contains('is-open')) return;
					self.drawerEl.hidden = true;
					self.returnDrawerHome();
				}, 300);
			}

			returnDrawerHome() {
				if (this._drawerHome && this.drawerEl.parentElement === document.body) {
					this._drawerHome.appendChild(this.drawerEl);
				}
				this._drawerHome = null;
			}

			onDrawerClick(event) {
				if (
					event.target.closest('[data-mtm-drawer-close]') ||
					event.target.closest('[data-mtm-drawer-overlay]')
				) {
					event.preventDefault();
					this.closeDrawer();
				}
			}

			// A drawer parked on <body> would outlive the creator on a section re-render.
			disconnectedCallback() {
				if (this.drawerEl && this.drawerEl.parentElement === document.body) {
					this.drawerEl.remove();
				}
				this._drawerHome = null;
				if (this._onDrawerKeydown) {
					document.removeEventListener('keydown', this._onDrawerKeydown);
					this._onDrawerKeydown = null;
				}
				document.documentElement.classList.remove('mtm-scroll-locked');
			}
		};

		if (!window.customElements.get('mtm-creator')) {
			window.customElements.define('mtm-creator', Creator);
		}
	}

	defineCreator();
})();
